"use client";

// /test1 — the constellation landing draft (Figma 1795:5262 / web 1795:5304,
// developer depictions 125:676 · 125:714 · 125:739).
//
// THE SHAPE — one page, one pinned act, ONE living netti:
//   hero      netti is born small at the centre, grows, and settles into the
//             top-centre crown — while the surfaces and folders it holds
//             ORBIT it, slowly and forever, in the dashboard orbit's own
//             geometry (LandingOrbit). The words hold the bottom third.
//   the track connect · create · carry — ONE tall track with a pinned stage
//             (the app's pinned-act law). NINE segments step through beneath
//             the acts ticker — the dashboard navigator's own behaviour: the
//             lit act glides to the FAR LEFT, finished acts slide off blurred,
//             and a new act is only offered once it is being worked on.
//   the bridge the LAST carry segment is the hand-off: everything fades and
//             the creature alone rises into the full-bleed section below.
//   meet      netti centre stage; "meet netti" beneath it, centre-aligned. As
//             you scroll on, the words carry the momentum out to the right and
//             fade while the creature locks to the top-centre for the helps.
//   privacy   the padlock, one word, and six sealed cards — panned through
//             left-to-right as you scroll (a swipeable ticker on a phone).
//   develop   the developer act: the ground turns technical green and three
//             thread depictions (git-for-thinking · idea branching · the
//             activity log) grow out of the creature in the constellation's
//             own brush (ThreadFig).
//
// SCROLL-TIMED, NOT SCROLL-TIED — crossing a segment boundary TRIGGERS a
// timed transition (panes slide by the section-slide law; the ground tweens);
// the few deliberate exceptions (the activity ride, the meet exit, the privacy
// pan) write ONE compositor-only transform per scroll frame.
//
// ONE CONTINUOUS CREATURE — netti never dies down and reappears: every seat
// change is a single glide of position, scale, colour and age (the de-age/
// regrow cut is gone), so the whole page reads as one body morphing through
// moods. Under heavy frost it publishes calm so the engine winds down.

import { useHeroDoor } from "../heroDoor";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./test1.css";

import MemoryOrganism from "../../MemoryOrganism";
import { hslToRgb, type ColorState } from "../../../lib/organism/color";
import { setOrganismCalm } from "../../../lib/organism/calmBus";
import { luminance } from "../../../lib/theme";
import { openCreateYours } from "../../onboarding/CreateYours";
import { CtaStack } from "../home/NettiCta";
import HomeFooter from "../home/HomeFooter";
import RoomPrivacy from "../home/RoomPrivacy";
import "../home/home.css";
import "../home/launch.css";
import { LinkBar } from "../../skills/PasteUrl";
import { useCopy } from "../home/bits";
import { gateOpen } from "../../../lib/release/gates";
import SiteMenu from "../../SiteMenu";
import BrandIcon from "../../BrandIcon";
import AppIcon from "../../brand/AppIcon";
import OrganismSpinner from "../../brand/OrganismSpinner";
import DirIcon, { DirIconScaleStyle } from "../../os/DirIcon";
import CellAvatar from "../../CellAvatar";
import { FaqSection, FooterCta } from "../LandingSections";
import { VEIL_DOTS } from "../../../lib/organism/shaders";
import LandingOrbit, { type OrbitItem } from "./LandingOrbit";
import BlogRail from "./BlogRail";
import type { CompanyPost } from "../../../lib/company/types";
import { originName, type CommunitySkill } from "../../../lib/community/types";
// the commons' own stylesheet — the create act's skills view shows the SAME
// card, so it wears the same rules rather than a second copy that can drift.
import "../../community/community.css";
import { CO_RED } from "../../company/companyTheme";
import ThreadFig, { type FigAnchor, type FigPath } from "./ThreadFig";

/* ────────────────────────── the colour journey ────────────────────────── */

type HSL = { h: number; s: number; l: number };

type Look = {
  g: HSL; // the ground (body background)
  /** optional gradient poles — when present the ground becomes a full
   *  top→g→bottom wash (the hero's forest-to-teal breath) instead of a flat
   *  colour under the crown glow. */
  gT?: HSL;
  gB?: HSL;
  org: ColorState;
  x: number; // organism anchor, viewport fractions
  y: number;
  xN?: number; // the anchor a phone uses instead — a seat that is off to one
  yN?: number; // side on a desktop has nowhere to go on a 390px screen
  sc: number; // wide scale
  scN: number; // narrow scale
  op: number; // organism opacity
  veil: number; // frost opacity (resident ≥ 0.01)
  age: number;
  /** 0..1 — the developer act's venation/nodes clarity. 0 maps back onto the
   *  engine's own mint (venation 0.44 / nodes 0.42), so a look without it can
   *  never leave the creature drier than it was minted. */
  veins?: number;
  /** 0..1 — the glass membrane channel (privacy's technical creature); 0 maps
   *  back onto the mint (glass 0.55). */
  glass?: number;
  /** radians — an explicit turn of the body for a look (the introduction's
   *  panels each turn it further). Undefined = leave the rotation alone. */
  rot?: number;
  /** 0..1 — a fuller body: abundance + motes rise (the close's netti). 0 maps
   *  back onto the mint (abundance 0.55 / motes 0.5). */
  cells?: number;
};

// netti's palette follows the ground so every stage reads as one breath
const orgFor = (g: HSL, light = 0.58): ColorState => ({
  primary: { hue: (g.h + 4) % 360, sat: 0.68, light },
  secondary: { hue: (g.h + 320) % 360, sat: 0.6, light: light - 0.08 },
  tinge: { hue: (g.h + 34) % 360, sat: 0.56, light: light - 0.02 },
  contrast: 0.36,
});

// the hero ground is a PUNCHY BLUE now (John, 2026-08-20 — the green tint out):
// a near-navy crown behind the bloom, a saturated cobalt body, opening into a
// brighter azure by the words. ⚠️ .t1-boot in test1.css mirrors HERO_G — change
// them together or the boot veil lifts onto a different ground.
const HERO_G: HSL = { h: 222, s: 0.72, l: 0.3 };
const HERO_GT: HSL = { h: 230, s: 0.78, l: 0.16 };
const HERO_GB: HSL = { h: 210, s: 0.85, l: 0.42 };
/* ── THE LANDING'S BODY — John's own /media composition ───────────────────
   Exported from the media constructor (Media Settings.json, 2026-08-17) and
   applied verbatim as the creature's SHAPE for the whole page: a long, narrow,
   sharp-tipped petal (petalLength .676 · petalWidth .157 · tipSharpness .053),
   densely formed (formation .864 · abundance .866), strongly veined and lit
   from within (venation .765 · innerLight .796) with almost no glass and NO
   central core (cellSize 0) — the bloom is all filament, no orb.

   It is dispatched ONCE at boot rather than carried on every look, because it
   describes the body, not the mood: each section still tints, ages, turns and
   frosts the same creature over the top of it. */
const MEDIA_NETTI: Record<string, number> = {
  formation: 0.864,
  radialBias: 0.644,
  abundance: 0.866,
  cellSize: 0,
  // the export's petals, opened a touch: a hair wider and softer-tipped so the
  // silhouette reads as bloom rather than needle, and the edge stops cutting
  petalLength: 0.66,
  petalWidth: 0.24,
  tipSharpness: 0.18,
  bend: 0.34,
  variety: 0.14,
  // MORE GLASS, LESS WIRE — the export's venation .765 read as a lit filament
  // body; at .5 with glass .34 the same shape blurs at its edges into the
  // ground the way John asked (the membrane carries the light, not the veins)
  glass: 0.34,
  venation: 0.5,
  nodes: 0.22,
  innerLight: 0.72,
  hueSpread: 0.446,
  revolve: 0.3,
  sway: 0.5,
  tempo: 0.45,
  breath: 0.4,
  motes: 0.3,
  moteSize: 0.35,
};

// the export's own palette — gold · cyan · violet, at its contrast 0.74. The
// one tweak: the gold is pulled a half-step warmer so it sits on the hero's
// teal ground instead of shouting over it.
// ⚠️ the PRIMARY hue is what the engine publishes as --organism-color — the
// ink the pill's mark, the folders and every "organism-coloured" thing wear.
// The export led with its GOLD, so the whole page went yellow-inked under a
// creature that reads blue-green on screen. The cyan leads now; the gold is
// the tinge, warming without ever leading.
// against the blue ground the creature leads with GREEN-TEAL (the complement
// that actually separates from cobalt), keeps a deep blue second so the body
// still ties into the ground, and tinges spring-green instead of gold — gold
// on blue read as mud. Primary publishes --organism-color, so the pill's mark
// and every organism-coloured thing goes green-teal with it.
const HERO_ORG: ColorState = {
  primary: { hue: 168, sat: 0.78, light: 0.58 },
  secondary: { hue: 232, sat: 0.72, light: 0.58 },
  tinge: { hue: 132, sat: 0.6, light: 0.62 },
  contrast: 0.74,
};

const MEET_G: HSL = { h: 262, s: 0.78, l: 0.46 };
// the netti act wears its OWN colours — the creature switches up here, away
// from the journey's blues: magenta petals, a rose under-body, a teal fringe.
const MEET_ORG: ColorState = {
  primary: { hue: 318, sat: 0.78, light: 0.64 },
  secondary: { hue: 352, sat: 0.66, light: 0.56 },
  tinge: { hue: 182, sat: 0.6, light: 0.66 },
  contrast: 0.38,
};

// NINE segments: connect ×3 · create ×3 · carry ×3.
// The last one (netti) is the bridge — it hands the pinned track over to the
// full-bleed creature below it.
const SEG_N = 10;
const SEG_G: HSL[] = [
  { h: 207, s: 0.82, l: 0.42 }, // mcp — the Figma vivid blue
  { h: 147, s: 0.72, l: 0.3 }, // api — the Figma green
  { h: 225, s: 0.52, l: 0.34 }, // extensions — slate indigo
  { h: 268, s: 0.5, l: 0.26 }, // create · projects — deep violet
  { h: 208, s: 0.62, l: 0.3 }, // create · tasks — blue
  { h: 176, s: 0.6, l: 0.26 }, // create · orbit — deep teal (a light ground lost the labels)
  { h: 32, s: 0.5, l: 0.9 }, // carry · you — warm paper (the ink flips dark)
  { h: 118, s: 0.42, l: 0.88 }, // carry · activity — light mint
  { h: 28, s: 0.44, l: 0.16 }, // carry · story — a warm ground for the account
  { h: 258, s: 0.72, l: 0.42 }, // the hand-off — the violet the meeting lands on
];
// netti's SEAT per segment. It does NOT shrink to get out of the way — a
// shrunken creature at the back reads as a logo, not a presence. It stays big
// and the VEIL does the receding (law: the frost recedes, the body doesn't).
const TOP_SEAT = { x: 0.5, y: 0.0, sc: 1.02, scN: 0.86 };
const HOME_SEAT = { x: 0.5, y: 0.99, sc: 0.92, scN: 0.72 };
const segLook = (i: number): Look => {
  const g = SEG_G[i];
  // connect — netti crowns the top, full-bodied, deep under frost
  if (i < 3) return { g, org: orgFor(g), ...TOP_SEAT, op: 0.62, veil: 0.62, age: 0.74 };
  // create · skills: a step darker and quieter so the lifted cards hold their contrast
  if (i === 5) return { g, org: orgFor(g, 0.48), x: 0.5, y: 1.0, sc: 1.06, scN: 0.85, op: 0.8, veil: 0.03, age: 0.78 };
  // carry · you — a light ground, netti low and calm behind the reading
  if (i === 6) return { g, org: orgFor(g, 0.5), ...HOME_SEAT, op: 0.82, veil: 0.3, age: 0.86 };
  // carry · activity — the feed scroll drives the age from here (see the
  // activity pane's own scroll handler); this is the resting end of that ramp
  if (i === 7) return { g, org: orgFor(g, 0.5), ...HOME_SEAT, op: 0.8, veil: 0.34, age: 0.92 };
  // carry · story — the narrative arc, on a warm ground of its own
  if (i === 8) return { g: { h: 28, s: 0.44, l: 0.16 }, org: orgFor({ h: 28, s: 0.44, l: 0.16 }, 0.56), ...HOME_SEAT, op: 0.82, veil: 0.3, age: 0.95 };
  // carry · the HAND-OFF. The chrome has faded and the creature blooms straight
  // into the seat the meeting uses — same x/y/scale as LOOKS.meet0 — so the
  // next section is a CHANGE OF LIGHT, not another journey across the screen.
  if (i === 9) return { g: MEET_G, org: MEET_ORG, x: 0.5, y: 0.42, sc: 1.12, scN: 0.88, op: 1, veil: 0.02, age: 0.9 };
  return { g, org: orgFor(g), ...HOME_SEAT, op: 0.95, veil: 0.07, age: i === 3 ? 0.62 : 0.72 };
};

// privacy — the ground goes DEEP dark blue and the frost comes up with it;
// the creature turns technical glass with purple and brown tints.
const PRIV_ORG: ColorState = {
  primary: { hue: 346, sat: 0.92, light: 0.6 },
  secondary: { hue: 318, sat: 0.8, light: 0.58 },
  tinge: { hue: 16, sat: 0.9, light: 0.6 },
  contrast: 0.6,
};

// develop — the one depiction ground: the technical green, the creature
// GLASSY-VEINED for the act (the `veins` channel below).
const DEV0_G: HSL = { h: 96, s: 0.45, l: 0.13 };
// (2026-10-22: a step deeper, the neon green swamped the words above it)
const DEV0_ORG: ColorState = {
  primary: { hue: 118, sat: 0.46, light: 0.36 },
  secondary: { hue: 92, sat: 0.42, light: 0.28 },
  tinge: { hue: 150, sat: 0.44, light: 0.42 },
  contrast: 0.4,
};

// the three looks the introduction steps through after the meeting — a cool
// electric blue, a sea green, a warm amber — different enough to read as
// different creatures, one body throughout.
const MEET1_ORG: ColorState = {
  primary: { hue: 214, sat: 0.8, light: 0.64 },
  secondary: { hue: 258, sat: 0.6, light: 0.56 },
  tinge: { hue: 186, sat: 0.7, light: 0.7 },
  contrast: 0.4,
};
const MEET2_ORG: ColorState = {
  primary: { hue: 158, sat: 0.62, light: 0.58 },
  secondary: { hue: 120, sat: 0.5, light: 0.46 },
  tinge: { hue: 46, sat: 0.6, light: 0.68 },
  contrast: 0.42,
};
const MEET3_ORG: ColorState = {
  primary: { hue: 30, sat: 0.78, light: 0.62 },
  secondary: { hue: 352, sat: 0.6, light: 0.54 },
  tinge: { hue: 52, sat: 0.7, light: 0.72 },
  contrast: 0.4,
};

const TAIL_ORG: ColorState = {
  primary: { hue: 206, sat: 0.7, light: 0.52 },
  secondary: { hue: 240, sat: 0.52, light: 0.42 },
  tinge: { hue: 178, sat: 0.56, light: 0.58 },
  contrast: 0.44,
};

const LOOKS: Record<
  "boot" | "hero" | "meet0" | "meet1" | "meet2" | "meet3" | "tail" | "privacy" | "faq" | "blog" | "dev0",
  Look
> = {
  // BOOT — netti is born tiny at the centre of the solid ground, then (one
  // glide) blooms fully into the hero crown. The whole entrance is one tween.
  boot: { g: HERO_G, gT: HERO_GT, gB: HERO_GB, org: HERO_ORG, x: 0.5, y: 0.42, sc: 0.22, scN: 0.2, op: 0.6, veil: 0.01, age: 0.06 },
  // HERO — fully aged, expansive: the bloom owns the whole upper half.
  // the media body is far denser than the old bloom, so the same scale swamped
  // the words — it crowns at 1.3 and the copy keeps its own air
  hero: { g: HERO_G, gT: HERO_GT, gB: HERO_GB, org: HERO_ORG, x: 0.5, y: 0.03, sc: 1.3, scN: 0.98, op: 0.9, veil: 0.01, age: 0.97 },
  // MEET 0 — the creature owns the centre of the screen; the words sit beneath.
  meet0: { g: MEET_G, org: MEET_ORG, x: 0.5, y: 0.42, sc: 1.12, scN: 0.88, op: 1, veil: 0.02, age: 0.92, rot: 0 },
  // MEET 1..3 — netti locks to the top-centre and each panel gives it a new
  // colour and a further turn, so the introduction is also a small tour of
  // what the body can look like.
  meet1: { g: { h: 232, s: 0.7, l: 0.4 }, org: MEET1_ORG, x: 0.5, y: 0.04, sc: 1.02, scN: 0.82, op: 0.95, veil: 0.04, age: 0.9, rot: 0.55 },
  meet2: { g: { h: 168, s: 0.55, l: 0.3 }, org: MEET2_ORG, x: 0.5, y: 0.04, sc: 1.02, scN: 0.82, op: 0.95, veil: 0.04, age: 0.94, rot: 1.1 },
  meet3: { g: { h: 22, s: 0.62, l: 0.44 }, org: MEET3_ORG, x: 0.5, y: 0.04, sc: 1.02, scN: 0.82, op: 0.95, veil: 0.04, age: 0.97, rot: 1.65 },
  // faq — a light sky; netti keeps the empty centre-left beside the questions
  // on a desktop, and rests at the bottom on a phone.
  // faq — a light sky; the creature TONED DOWN (a paler, less saturated body at
  // lower opacity behind a touch more frost) so it reads as company to the
  // questions rather than competing with them
  faq: {
    g: { h: 186, s: 0.36, l: 0.88 },
    org: {
      primary: { hue: 190, sat: 0.42, light: 0.7 },
      secondary: { hue: 168, sat: 0.36, light: 0.66 },
      tinge: { hue: 214, sat: 0.4, light: 0.74 },
      contrast: 0.26,
    },
    x: 0.24, y: 0.52, xN: 0.5, yN: 1.04, sc: 0.9, scN: 0.6, op: 0.62, veil: 0.14, age: 0.9,
  },
  // blog — the COMPANY page's own world carried in: its pink-tinted ground and
  // its red creature (CO_RED, verbatim), netti at the far-right centre so the
  // rail owns the left. One product, one blog, one colour for it.
  blog: {
    g: { h: 300, s: 0.4, l: 0.86 },
    org: CO_RED,
    x: 0.86, y: 0.5, xN: 0.5, yN: 1.06, sc: 0.98, scN: 0.62, op: 0.9, veil: 0.06, age: 0.94,
  },
  // privacy — the evolved homepage's room (looks.ts `privacy`, 2026-10-22): a
  // black-cherry ground, netti seated at the top-right edge, the cards own the
  // middle. Out from behind the words, so no frost is needed to read them.
  privacy: {
    g: { h: 344, s: 0.6, l: 0.075 }, gT: { h: 340, s: 0.6, l: 0.05 }, gB: { h: 350, s: 0.62, l: 0.12 },
    org: PRIV_ORG, x: 1.0, y: 0.16, xN: 0.5, yN: -0.06, sc: 0.44, scN: 0.5, op: 0.8, veil: 0.01, age: 0.95,
  },
  dev0: { g: DEV0_G, org: DEV0_ORG, x: 0.5, y: 1.0, xN: 0.5, yN: 0.965, sc: 1.12, scN: 0.9, op: 0.64, veil: 0.02, age: 0.95, veins: 0.8, glass: 0.4 },
  // the close — a bigger, fuller creature (abundance up: many more cells) in a
  // deep sea blue, crowning the top-right of the last screen
  tail: { g: { h: 218, s: 0.65, l: 0.3 }, org: TAIL_ORG, x: 0.84, y: 0.16, xN: 0.5, yN: 0.06, sc: 1.05, scN: 0.7, op: 0.84, veil: 0.1, age: 0.99, cells: 1 },
};

const lookFor = (key: string): Look => {
  // the you segment is tinted by whoever is on stage: acts6p<i>
  if (key.startsWith("acts6p")) return youLook(Number(key.slice(6)) || 0);
  // ⚠️ THE GAP. Between the acts track's end (seg → -1) and the meet track's
  // top crossing the stage line there is a sliver of scroll where no segment
  // is on and no free section has taken over. It used to fall back to HERO —
  // so the creature set off for the hero crown at the top of the screen and
  // was then pulled back to the centre by meet0: the "up and back" wobble.
  // The bare "acts" stage now means "hold the hand-off's seat", which IS the
  // meeting's seat, so the two sections share one still creature.
  if (key === "acts") return segLook(SEG_N - 1);
  if (key.startsWith("acts")) return segLook(Number(key.slice(4)));
  // an unknown section must NEVER return undefined — the tween dereferences
  // the look, and an undefined here crashed the page below the netti bridge.
  return LOOKS[key as keyof typeof LOOKS] ?? LOOKS.hero;
};

/* small colour math (shortest-arc hues, like the live landing's) */
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpHue = (a: number, b: number, t: number) => {
  const d = ((b - a + 540) % 360) - 180;
  return (a + d * t + 360) % 360;
};
const lerpHSL = (a: HSL, b: HSL, t: number): HSL => ({ h: lerpHue(a.h, b.h, t), s: lerp(a.s, b.s, t), l: lerp(a.l, b.l, t) });
type Stop = { hue: number; sat: number; light: number };
const lerpStop = (a: Stop, b: Stop, t: number): Stop => ({ hue: lerpHue(a.hue, b.hue, t), sat: lerp(a.sat, b.sat, t), light: lerp(a.light, b.light, t) });
const lerpOrg = (a: ColorState, b: ColorState, t: number): ColorState => ({
  primary: lerpStop(a.primary, b.primary, t),
  secondary: lerpStop(a.secondary, b.secondary, t),
  tinge: lerpStop(a.tinge ?? a.primary, b.tinge ?? b.primary, t),
  contrast: lerp(a.contrast ?? 0.35, b.contrast ?? 0.35, t),
});
const lerpLook = (a: Look, b: Look, t: number): Look => ({
  g: lerpHSL(a.g, b.g, t),
  // gradient poles fall back to the flat ground, so a wash breathes in and
  // out of existence instead of snapping
  gT: a.gT === undefined && b.gT === undefined ? undefined : lerpHSL(a.gT ?? a.g, b.gT ?? b.g, t),
  gB: a.gB === undefined && b.gB === undefined ? undefined : lerpHSL(a.gB ?? a.g, b.gB ?? b.g, t),
  org: lerpOrg(a.org, b.org, t),
  x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t),
  // a look without a narrow seat falls back to its wide one, so a phone tween
  // between "has one" and "doesn't" still interpolates a real number
  xN: lerp(a.xN ?? a.x, b.xN ?? b.x, t), yN: lerp(a.yN ?? a.y, b.yN ?? b.y, t),
  sc: lerp(a.sc, b.sc, t), scN: lerp(a.scN, b.scN, t),
  op: lerp(a.op, b.op, t), veil: lerp(a.veil, b.veil, t), age: lerp(a.age, b.age, t),
  // the veins/glass channels only exist while at least one endpoint declares
  // them — everywhere else nothing is dispatched and the mint stays untouched.
  veins: a.veins === undefined && b.veins === undefined ? undefined : lerp(a.veins ?? 0, b.veins ?? 0, t),
  glass: a.glass === undefined && b.glass === undefined ? undefined : lerp(a.glass ?? 0, b.glass ?? 0, t),
  rot: a.rot === undefined && b.rot === undefined ? undefined : lerp(a.rot ?? 0, b.rot ?? 0, t),
  cells: a.cells === undefined && b.cells === undefined ? undefined : lerp(a.cells ?? 0, b.cells ?? 0, t),
});
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/* ── THE HERO ORBIT — what circles the newborn creature ───────────────────
   The connection lines are gone; the hero now says the same thing the way the
   dashboard says it — an orbit. The set alternates the surfaces that already
   listen (their real tiles) with the kinds of things a memory holds (folders
   and pages wearing an emoji relevant to their name), newest-first so the
   heaviest presences take the crown. */
const HERO_ORBIT: OrbitItem[] = [
  { key: "claude", kind: "app", app: "claude", name: "claude" },
  { key: "launch plan", kind: "folder", name: "launch plan", symbol: "🚀" },
  { key: "chatgpt", kind: "app", app: "openai", name: "chatgpt" },
  { key: "journal", kind: "folder", name: "journal", symbol: "📓" },
  { key: "cursor", kind: "app", app: "cursor", name: "cursor" },
  { key: "the album", kind: "folder", name: "the album", symbol: "🎧" },
  { key: "gemini", kind: "app", app: "gemini", name: "gemini" },
  { key: "clients", kind: "folder", name: "clients", symbol: "🤝" },
  { key: "side project", kind: "folder", name: "side project", symbol: "🛠️" },
  { key: "chrome", kind: "app", app: "chrome", name: "chrome" },
  { key: "recipes", kind: "folder", name: "recipes", symbol: "🍋" },
  { key: "notion", kind: "app", app: "notion", name: "notion" },
  { key: "code reviews", kind: "folder", name: "code reviews", symbol: "🔍" },
  { key: "travel notes", kind: "folder", name: "travel notes", symbol: "🧭" },
  { key: "gmail", kind: "app", app: "gmail", name: "gmail" },
  { key: "api sketches", kind: "page", name: "api sketches", symbol: "⚙️" },
  { key: "moodboard", kind: "page", name: "moodboard", symbol: "🎨" },
  { key: "reading list", kind: "folder", name: "reading list", symbol: "📚" },
  { key: "standup notes", kind: "page", name: "standup notes", symbol: "📋" },
  { key: "bug diary", kind: "page", name: "bug diary", symbol: "🐛" },
  { key: "perplexity", kind: "app", app: "perplexity", name: "perplexity" },
  { key: "playlists", kind: "folder", name: "playlists", symbol: "🎧" },
  { key: "linear", kind: "app", app: "linear", name: "linear" },
  { key: "grant draft", kind: "page", name: "grant draft", symbol: "📝" },
  { key: "slack", kind: "app", app: "slack", name: "slack" },
  { key: "interviews", kind: "folder", name: "interviews", symbol: "🎤" },
  { key: "windsurf", kind: "app", app: "windsurf", name: "windsurf" },
  { key: "week plan", kind: "page", name: "week plan", symbol: "🗓️" },
  { key: "deepseek", kind: "app", app: "deepseek", name: "deepseek" },
  { key: "garden log", kind: "folder", name: "garden log", symbol: "🌱" },
  // ── the tail exists to FILL THE CIRCLE ───────────────────────────────────
  // On a phone the field is a grid of three full circles and every item holds
  // exactly one seat (nothing ever repeats in a glance). Those circles have
  // ~44 seats between them; a shorter pool doesn't make the field denser, it
  // makes every ring thin at once and opens gaps you can watch drift past. So
  // the pool is stocked past the seat count — variety around the circle, never
  // extra density. The desktop hero is unaffected: its curated 9 · 6 crown is
  // capped by `ringCounts`, not by how long this list is.
  { key: "figma", kind: "app", app: "figma", name: "figma" },
  { key: "grocery list", kind: "page", name: "grocery list", symbol: "🥕" },
  { key: "github", kind: "app", app: "github", name: "github" },
  { key: "house move", kind: "folder", name: "house move", symbol: "📦" },
  { key: "obsidian", kind: "app", app: "obsidian", name: "obsidian" },
  { key: "thesis", kind: "folder", name: "thesis", symbol: "🎓" },
  { key: "zed", kind: "app", app: "zed", name: "zed" },
  { key: "gift ideas", kind: "page", name: "gift ideas", symbol: "🎁" },
  { key: "codex", kind: "app", app: "codex", name: "codex" },
  { key: "sea swims", kind: "folder", name: "sea swims", symbol: "🌊" },
  { key: "cline", kind: "app", app: "cline", name: "cline" },
  { key: "one pager", kind: "page", name: "one pager", symbol: "📄" },
  { key: "arc", kind: "app", app: "arc", name: "arc" },
  { key: "birthdays", kind: "folder", name: "birthdays", symbol: "🎂" },
  { key: "ollama", kind: "app", app: "ollama", name: "ollama" },
  { key: "packing list", kind: "page", name: "packing list", symbol: "🧳" },
  { key: "obsidian notes", kind: "folder", name: "night notes", symbol: "🌙" },
  { key: "letters", kind: "folder", name: "letters", symbol: "✉️" },
  { key: "receipts", kind: "page", name: "receipts", symbol: "🧾" },
  { key: "the flat", kind: "folder", name: "the flat", symbol: "🪴" },
];

/* ─────────────────────────── small shared bits ────────────────────────── */

/** the endpoint, on THE link bar (components/skills/PasteUrl): the same bar the
 *  commons and the evolved homepage hand a link over with. */
function EndpointRow({ url }: { url: string }) {
  const [done, copy] = useCopy(`https://${url}`);
  return <LinkBar url={`https://${url}`} shown={url} done={done} onCopy={copy} label="copy the link" />;
}

/** a copyable code block — the one call, and the button that hands it over.
 *  Same confirm-itself behaviour as the endpoint bar: the label becomes the
 *  receipt, and the code stays selectable if the clipboard is denied. */
function CopyBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="t1-req__wrap">
      <pre className="t1-req">{code}</pre>
      <button
        type="button"
        className="t1-req__copy"
        data-copied={copied || undefined}
        onClick={() => {
          try {
            void navigator.clipboard?.writeText(code);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          } catch {
            /* the block is on screen and selectable — that fallback always works */
          }
        }}
      >
        {copied ? "copied" : "copy"}
      </button>
    </div>
  );
}

/** scroll-TIMED reveal for the free sections: entering once starts the
 *  sequence; it plays itself. */
function useReveal() {
  const obs = useRef<IntersectionObserver | null>(null);
  useEffect(() => () => obs.current?.disconnect(), []);
  return useCallback((el: HTMLElement | null) => {
    if (!el) return;
    if (!obs.current) {
      obs.current = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              (e.target as HTMLElement).setAttribute("data-in", "1");
              obs.current?.unobserve(e.target);
            }
          }
        },
        { threshold: 0.2 },
      );
    }
    obs.current.observe(el);
  }, []);
}

/* ─────────────────── the create act, in the app's grammar ──────────────── */

const GRID_ITEMS: { n: string; icon: "folder" | "page"; symbol: string; sel?: boolean }[] = [
  { n: "clients", icon: "folder", symbol: "🤝", sel: true },
  { n: "brand voice", icon: "folder", symbol: "🎙️" },
  { n: "research", icon: "folder", symbol: "🔭" },
  { n: "launch plan", icon: "folder", symbol: "🚀" },
  { n: "side project", icon: "folder", symbol: "🛠️" },
  { n: "reading list", icon: "folder", symbol: "📚" },
  { n: "welcome copy", icon: "page", symbol: "✏️" },
  { n: "moodboard", icon: "page", symbol: "🎨" },
];

// tasks organised UNDER their projects — the folders become the group labels
const TASK_GROUPS: { proj: string; tasks: { t: string; done?: boolean }[] }[] = [
  { proj: "clients", tasks: [{ t: "call with ana", done: true }, { t: "send the moodboard" }] },
  { proj: "launch plan", tasks: [{ t: "draft the landing copy" }, { t: "book the studio", done: true }] },
  { proj: "brand voice", tasks: [{ t: "collect five references" }] },
];

// the orbit takes everything — folders, pages, artifacts — each with its date,
// newest first so the freshest work rides the crown (the dashboard's own law).
// Every face wears an emoji relevant to its name; most of the field is folders.
// PARKED, not deleted — the create act's orbit view is hidden for now (its pane
// shows the commons' skills instead). The pool stays so bringing the depiction
// back is re-mounting <LandingOrbit>, not re-authoring thirty items.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CREATE_ORBIT: OrbitItem[] = [
  { key: "welcome copy", kind: "page", name: "welcome copy", symbol: "✏️", meta: "1h ago" },
  { key: "clients", kind: "folder", name: "clients", symbol: "🤝", meta: "2h ago" },
  { key: "brand voice", kind: "folder", name: "brand voice", symbol: "🎙️", meta: "yesterday" },
  { key: "call with ana", kind: "page", name: "call with ana", symbol: "☎️", meta: "2d ago" },
  { key: "research", kind: "folder", name: "research", symbol: "🔭", meta: "3d ago" },
  { key: "moodboard", kind: "page", name: "moodboard", symbol: "🎨", meta: "tuesday" },
  { key: "launch plan", kind: "folder", name: "launch plan", symbol: "🚀", meta: "may 12" },
  { key: "pricing notes", kind: "page", name: "pricing notes", symbol: "🧮", meta: "may 9" },
  { key: "reading list", kind: "folder", name: "reading list", symbol: "📚", meta: "3w ago" },
  { key: "field notes", kind: "page", name: "field notes", symbol: "🌿", meta: "april" },
  { key: "api sketches", kind: "page", name: "api sketches", symbol: "⚙️", meta: "april" },
  { key: "user interviews", kind: "folder", name: "user interviews", symbol: "🎤", meta: "april" },
  { key: "inspo shots", kind: "folder", name: "inspo shots", symbol: "📷", meta: "march" },
  { key: "v2 spec", kind: "page", name: "v2 spec", symbol: "📐", meta: "march" },
  { key: "first ideas", kind: "folder", name: "first ideas", symbol: "🌱", meta: "march" },
  // five more so the unique circle has enough variety to fill its seats — the
  // spacing floors in LandingOrbit decide how many actually appear, so a longer
  // pool buys variety around the circle, never density.
  { key: "press kit", kind: "folder", name: "press kit", symbol: "📰", meta: "may" },
  { key: "roadmap", kind: "page", name: "roadmap", symbol: "🧭", meta: "june" },
  { key: "team notes", kind: "folder", name: "team notes", symbol: "📒", meta: "3w ago" },
  { key: "demo script", kind: "page", name: "demo script", symbol: "🎬", meta: "may" },
  { key: "site copy", kind: "folder", name: "site copy", symbol: "📝", meta: "april" },
  { key: "budget", kind: "page", name: "budget", symbol: "🧾", meta: "june" },
  { key: "q3 plan", kind: "folder", name: "q3 plan", symbol: "📆", meta: "june" },
  { key: "old drafts", kind: "folder", name: "old drafts", symbol: "🗃️", meta: "feb" },
  { key: "wireframes", kind: "page", name: "wireframes", symbol: "📱", meta: "may" },
  { key: "brainstorm", kind: "folder", name: "brainstorm", symbol: "💡", meta: "april" },
  { key: "changelog", kind: "page", name: "changelog", symbol: "📜", meta: "june" },
  { key: "onboarding", kind: "folder", name: "onboarding", symbol: "🛬", meta: "may" },
  { key: "assets", kind: "folder", name: "assets", symbol: "📦", meta: "march" },
  { key: "meeting notes", kind: "page", name: "meeting notes", symbol: "☕", meta: "2d ago" },
];

/* the surfaces row in the connect act — the Figma icon line, tail fading out */
/* ── carry · you — four imagined people, one per dot ─────────────────────
   The evolved /you: instead of arguing that memory matters, it shows four
   lives it is already holding — one real-world founder imagined kindly, three
   invented — each with their own netti, their own ground, their own shelf of
   memory. Clicking a face turns the whole stage to that person. The fine
   print owns the conceit: imagined people, imaginative avatars. */
const YOU_PEOPLE: {
  k: string; name: string; label: string; who: string; hue: number; g: HSL; age: number;
  notices: { t: string; d: string }[];
}[] = [
  {
    k: "maya", name: "maya", label: "a founder", hue: 42, age: 0.88,
    g: { h: 42, s: 0.24, l: 0.14 },
    who: "deciding ten things a day with three tools open",
    notices: [
      { t: "referral program, killed in january — cac was 3x organic",
        d: "tested for six weeks, tracked against every other channel, and shelved for a specific number. when it came back up in a strategy meeting, the reasoning was already there — nobody had to re run the experiment to remember why it didn't work." },
      { t: "investor promised 15% mom growth by q3",
        d: "said once, in a follow up email thread, not in the deck. it surfaced the moment the same investor asked for an update, instead of getting buried under forty other emails from that week." },
      { t: "co founder agreement: equal split unless one of us raises solo",
        d: "decided at 11pm, never formalized. the kind of conversation that normally exists in one person's memory until it doesn't. this one didn't depend on anyone remembering it right." },
    ],
  },
  {
    k: "john", name: "john", label: "a developer", hue: 205, age: 0.82,
    g: { h: 210, s: 0.42, l: 0.13 },
    who: "shipping from claude code and cursor, one memory in both",
    notices: [
      { t: "picked drizzle over prisma — needed raw sql for the migration",
        d: "a decision made under deadline pressure, for a reason specific to that one migration. six weeks later, a teammate almost reopened the debate. the original constraint was still attached to the decision, not lost with the sprint." },
      { t: "null pointer in the webhook handler — fixed by checking payload.user first",
        d: "solved once, in a project most people forgot existed. it resurfaced in a different repo with the same error, and the fix was already sitting there instead of costing another hour of debugging." },
      { t: "cursor: never auto format on save, breaks the diff review",
        d: "a preference stated once, mid task, easy to forget existed. it didn't need repeating the next time the tool defaulted back to its own behavior." },
    ],
  },
  {
    k: "sam", name: "sam", label: "a teacher", hue: 148, age: 0.86,
    g: { h: 150, s: 0.3, l: 0.15 },
    who: "teaching the same syllabus to a different room every year",
    notices: [
      { t: "week four: photosynthesis lecture loses the room — no diagram",
        d: "flagged the first year it happened, forgotten by the second. this year the diagram came before the definition, because the reason it mattered was still there when the syllabus got rebuilt." },
      { t: "the mitosis analogy that actually lands: a factory assembly line",
        d: "found once, three years ago, in the middle of a good class. the kind of thing that normally gets rediscovered by accident every few semesters. this time it didn't have to be." },
      { t: "moved lesson 6 to week 2 — needs lesson 9's vocabulary first",
        d: "a sequencing fix made mid term, never written into the official syllabus. it stayed in place the following year without anyone having to remember the dependency by hand." },
    ],
  },
  {
    k: "ines", name: "ines", label: "a researcher", hue: 268, age: 0.9,
    g: { h: 266, s: 0.4, l: 0.15 },
    who: "holding six months of reading across four projects",
    notices: [
      { t: "2023 replication study: original effect size dropped by half",
        d: "read once, in march, buried in a paper that wasn't the main citation. it surfaced the moment the original study got pulled up again for a new draft, instead of getting cited as settled by mistake." },
      { t: "hypothesis a and b: evidence kept separate, not averaged",
        d: "a methodological call made early, easy to quietly abandon under deadline pressure. it held three drafts later, because the reasoning for keeping them apart was attached to the decision itself." },
      { t: "switched from survey to interview data in april — response bias",
        d: "the actual justification lived in a slack message, not the methods section. when a reviewer asked why, the reasoning was already there instead of needing to be reconstructed from memory." },
    ],
  },
];

/** the you segment's look, tinted by whoever is on stage */
function youLook(pi: number): Look {
  const per = YOU_PEOPLE[pi] ?? YOU_PEOPLE[0];
  return {
    g: per.g,
    org: {
      // a step deeper than the mint (2026-10-22): the noticed list reads over her
      primary: { hue: per.hue, sat: 0.74, light: 0.52 },
      secondary: { hue: (per.hue + 316) % 360, sat: 0.62, light: 0.44 },
      tinge: { hue: (per.hue + 38) % 360, sat: 0.66, light: 0.6 },
      contrast: 0.34,
    },
    x: 0.5, y: 0.99, sc: 0.98, scN: 0.74,
    op: 0.78, veil: 0.1, age: per.age,
  };
}

/* ── carry · activity — the dashboard's own river, with a project's growth
   written into it. Shape matches FeedActivity so the real components render
   it unchanged (source · time · where · verb · kind · the narrated line). ── */
const FEED: {
  id: string; source: string; time: string; where: string; verb: string; kind: string;
  before: string; cite: string; after: string; self?: boolean;
}[] = [
  { id: "f1", source: "Claude", time: "2h", where: "launch plan", verb: "kept", kind: "decision",
    before: "settled on ", cite: "two tiers, not three", after: ", and noted why the middle one kept collapsing." },
  { id: "f2", source: "Cursor", time: "5h", where: "launch plan", verb: "grew", kind: "dimension",
    before: "the pricing section of ", cite: "positioning", after: " picked up what you worked out in the other tool." },
  { id: "f3", source: "ChatGPT", time: "yesterday", where: "you", verb: "noticed", kind: "fact", self: true,
    before: "you keep returning to ", cite: "the same four questions", after: " when a decision is close." },
  { id: "f4", source: "Claude", time: "yesterday", where: "field notes", verb: "folded", kind: "artifact",
    before: "seven fragments became ", cite: "one document", after: ", restructured around what you actually kept." },
  { id: "f5", source: "innernet", time: "2d", where: "brand voice", verb: "retired", kind: "fact",
    before: "an old line stopped being true, so it was ", cite: "marked superseded", after: " rather than deleted." },
  { id: "f6", source: "Cursor", time: "4d", where: "launch plan", verb: "branched", kind: "idea",
    before: "you forked ", cite: "a quieter version", after: " and left the loud one running." },
  { id: "f7", source: "ChatGPT", time: "last week", where: "research", verb: "opened", kind: "project",
    before: "a stray question became ", cite: "a place to put things", after: "." },
  { id: "f8", source: "Claude", time: "june", where: "field notes", verb: "began", kind: "memory",
    before: "the first thing you ever saved here: ", cite: "a half-formed idea on a train", after: "." },
];

/* ── carry · STORY — the whole arc, not the beats ────────────────────────
   activity is per-beat: what landed, when. STORY is what those beats ADD UP
   TO — the account innernet can give of a project from its first note to
   where it stands, in the order it actually happened. It is the answer to
   "catch me up", which is a different question from "what changed". */
const STORY: { when: string; t: string; d: string }[] = [
  { when: "march", t: "it started as a complaint",
    d: "a note about re-explaining the same architecture to a new chat, three days running." },
  { when: "april", t: "the shape arrived",
    d: "one endpoint every tool could read. the alternatives are still here, with why each was set down." },
  { when: "may", t: "the first hard call",
    d: "no orm, for the migrations. the reasoning is attached, so it has not been re-argued since." },
  { when: "june", t: "it started keeping itself",
    d: "captures began folding on their own. the map stopped being something to maintain." },
  { when: "august", t: "where it stands",
    d: "two tiers, a live endpoint, and eleven decisions you can still read the reasons for." },
];

/* ── the surfaces that speak mcp ───────────────────────────────────────────
   ⚠️ EVERY TILE HERE MUST BE A CLIENT THAT CAN CONNECT TO INNERNET, because
   that is what the row claims. It carried `deepseek` (our inference provider —
   a model we call, not a tool that calls us) and `notion` (an mcp SERVER, the
   other end of the wire), neither of which anyone can connect. Both are out;
   `cline` and `continue` are in, and they were already on the honest list.
   The list of record is lib/landing/faqs.ts — keep the two in step. */
const MCP_ROW = [
  "claude", "openai", "codex", "cursor", "antigravity",
  "gemini", "windsurf", "zed", "cline", "continue",
];

/* ── the surfaces innernet is on, and the ones it is going to be on ───────
   The list is honest about its own state: a tag is only ever "live" for the
   three things that answer a request today (the hosted mcp endpoint, the rest
   api, the web app). Everything else says what it really is. */
/* Trimmed to the three doors NOT already covered by the mcp and api panes —
   the extensions pane is about what is still being cut, not a directory. */
type Tag = "live" | "pre-alpha" | "soon" | "exploring";
const SURFACES: { k: string; icon: string; name: string; d: string; tag: Tag; href?: string }[] = [
  { k: "ext", icon: "stack", name: "the browser extension", tag: "soon",
    d: "keep a page where you found it, without leaving it" },
  { k: "ours", icon: "ours", name: "ours, the shared space", tag: "soon",
    d: "a memory two people can stand in. every ai in the room reads it. it gets its own beginning" },
  { k: "mac", icon: "macapp", name: "the mac app", tag: "pre-alpha",
    d: "a quiet window on your desktop. keep anything, from anywhere" },
];

/* the three mini navigator views — what netti actually does for you, said
   three ways. Horizontal, and they swipe through on their own. */
/* THE INTRODUCTION — four panels, one creature. The first is the meeting; the
   next three say what netti IS, each more descriptive than a title, each with
   its own look for the body (see MEET_LOOKS): unique to you, growing with
   you, a reflection of what you keep. */
/** the intro plus the three panels — one constant, because the track height, the
 *  scroll stepping and the click-to-cycle all have to agree about how many beats
 *  the section has. */
const MEET_BEATS = 4;
const MEET_PANELS: { k: string; t: string; d: string }[] = [
  { k: "meet", t: "meet netti",
    d: "a growing reflection of your memories and connections. one per person, never the same twice. it grows as you keep things, and takes on the colour of what you keep." },
  { k: "yours", t: "no two are the same",
    d: "netti takes its shape and colour from what you keep, so yours can only ever look like yours." },
  { k: "grows", t: "it grows as you do",
    d: "every thing you keep adds a cell. netti ages, deepens and settles exactly as your memory does." },
  { k: "reflects", t: "a reflection you can watch change",
    d: "everything you have kept, and everyone you have kept it with, gathered into one shape that keeps becoming." },
];

/* ── DEVELOP WITH INNERNET — the three depictions (Figma 125:676 · 125:714 ·
      125:739), each authored as normalized thread paths + labeled anchor
      cells for ThreadFig. The threads grow OUT of the creature's seat in each
      look, so the depiction and the body read as one organism. ── */

// 1 · git for thinking — a map of named memory branching up out of the bloom.
const GIT_PATHS: FigPath[] = [
  { pts: [[0.5, 1.04], [0.4, 0.84], [0.24, 0.72]] },
  { pts: [[0.24, 0.72], [0.11, 0.75]], after: 0 },
  { pts: [[0.24, 0.72], [0.13, 0.58]], after: 0 },
  { pts: [[0.5, 1.04], [0.43, 0.78], [0.29, 0.56]] },
  { pts: [[0.5, 1.04], [0.45, 0.74], [0.39, 0.62]] },
  { pts: [[0.5, 1.04], [0.52, 0.72], [0.56, 0.52]] },
  { pts: [[0.5, 1.04], [0.62, 0.8], [0.74, 0.58], [0.82, 0.5]] },
  { pts: [[0.5, 1.04], [0.61, 0.86], [0.78, 0.76], [0.87, 0.72]] },
];
// the phone composition — five branches instead of eight, spread across the
// full height so no label ever meets another, the stem starting a touch
// lower (1.06) so it visibly rises out of the creature's core
const GIT_PATHS_N: FigPath[] = [
  { pts: [[0.5, 1.06], [0.36, 0.8], [0.14, 0.62]] },
  { pts: [[0.5, 1.06], [0.44, 0.72], [0.34, 0.36]] },
  { pts: [[0.5, 1.06], [0.54, 0.7], [0.6, 0.2]] },
  { pts: [[0.5, 1.06], [0.62, 0.8], [0.84, 0.48]] },
  { pts: [[0.5, 1.06], [0.6, 0.9], [0.86, 0.8]] },
];
const GIT_ANCHORS_N: FigAnchor[] = [
  { x: 0.14, y: 0.6, s: 30, label: "tone of voice", fd: 0.9 },
  { x: 0.34, y: 0.34, s: 30, label: "design_skill", fd: 0.85 },
  { x: 0.6, y: 0.18, s: 32, label: "connector memory", fd: 0.8 },
  { x: 0.84, y: 0.46, s: 30, label: "project vision", fd: 0.9 },
  { x: 0.86, y: 0.78, s: 28, label: "decisions", fd: 0.95 },
];
const GIT_ANCHORS: FigAnchor[] = [
  { x: 0.1, y: 0.75, s: 40, label: "tone of voice v1", fd: 0.86 },
  { x: 0.12, y: 0.56, s: 44, label: "tone of voice v2", fd: 0.95 },
  { x: 0.28, y: 0.53, s: 42, label: "design_skill", fd: 0.8 },
  { x: 0.39, y: 0.59, s: 46, label: "model costs", fd: 0.62 },
  { x: 0.56, y: 0.49, s: 48, label: "connector memory", fd: 0.68 },
  { x: 0.83, y: 0.47, s: 42, label: "project vision", fd: 0.9 },
  { x: 0.88, y: 0.69, s: 40, label: "decisions", fd: 0.85 },
  { x: 0.24, y: 0.72, s: 15, fd: 0.42 },
  { x: 0.43, y: 0.78, s: 13, fd: 0.3 },
  { x: 0.62, y: 0.8, s: 14, fd: 0.34 },
];

const ACT_OF_SEG = (s: number) => (s < 3 ? 0 : s < 6 ? 1 : 2);
// segment 9 is the wordless hand-off (see segLook) — carry's own views are 6..8
const ACTS = ["connect", "create", "carry"] as const;
const DEKS = [
  // the Figma line for the connect act; the mechanic it used to carry has moved
  // down into the mcp pane's own lead, where it sits next to the endpoint
  "innernet is available in a growing list of surfaces to connect.",
  "connected, it stops being a transcript and becomes something you can hold.",
  "memory that grows as you live it, and comes with you.",
];

/* ── the person on stage — the evolved /you, as a hero ────────────────────
   The dashboard's you-space, abstracted and given a face: an avatar wearing
   the cell, the name and what they are, the reflection chips, and the shelf
   of memory innernet is holding for them. Clicking the face advances to the
   next person; the strip picks one directly. The GROUND and the creature
   belong to whoever is on stage — that part is wired through the page's own
   look tween (lookKey `acts6p<i>`), so a click re-colours the whole world,
   not just the card. */
function PersonAvatar({ k, name, size }: { k: string; name: string; size: number }) {
  // THE face is CellAvatar (law_cell / law_cell_rim): the photo wears the cell
  // at the cell's own 1071×831 aspect — a square box stretched the silhouette
  // into the squarish orb it must never be — with the 2px rim seating it.
  // shadow OFF: CellAvatar's own glow is a filter on the clipped element, so it
  // renders inside the cell and reads as a grey box behind the face. The lift
  // lives on the (unclipped) button instead — see .t1-yu__facebtn.
  return <CellAvatar name={name} src={`/landing/you/${k}.webp`} size={size} shadow={false} className="t1-yu__face" />;
}

function YouPersona({ pi, onPick }: { pi: number; onPick: (i: number) => void }) {
  const p = YOU_PEOPLE[pi] ?? YOU_PEOPLE[0];
  return (
    <div className="t1-yu">
      <div className="t1-yu__lead">
        <button
          type="button"
          className="t1-yu__facebtn"
          onClick={() => onPick((pi + 1) % YOU_PEOPLE.length)}
          aria-label="next person"
        >
          <PersonAvatar k={p.k} name={p.name} size={112} />
        </button>
        <div className="t1-yu__who">
          <span className="t1-yu__name" key={p.k}>
            {p.name} <em>· {p.label}</em>
          </span>
          <span className="t1-yu__wholine">{p.who}</span>
          {/* the timer — four quiet dots; the lit one FILLS on the auto-advance
              cadence, and any dot is a direct door to its person */}
          <div className="t1-yu__dots" role="tablist" aria-label="whose memory">
            {YOU_PEOPLE.map((q, i) => (
              <button
                key={q.k}
                type="button"
                role="tab"
                aria-selected={i === pi}
                aria-label={`${q.name}, ${q.label}`}
                data-on={i === pi || undefined}
                onClick={() => onPick(i)}
              >
                <span key={`${q.k}-${pi}`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* the dashboard's own reading order — "noticed", then the notices. Each
          one is a DECISION the memory keeps working for the project, never a
          habit observed for its own sake. */}
      <div className="t1-yu__shelf" key={p.k}>
        <p className="t1-np__kicker">noticed</p>
        <div className="t1-yu__notices">
          {p.notices.map((n, i) => (
            <div key={n.t} className="t1-yu__notice" style={{ "--i": i } as React.CSSProperties}>
              <span className="t1-yu__dot" aria-hidden />
              <div>
                <span className="t1-yu__nt">{n.t}</span>
                <p className="t1-yu__nd">{n.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="t1-yu__fine">
        four imagined people, imaginative avatars. your own page grows from what you
        actually keep.
      </p>
    </div>
  );
}

/* ─────────────────────────────── the page ─────────────────────────────── *//* ─────────────────────────────── the page ─────────────────────────────── */

type Stage = "hero" | "acts" | "meet" | "privacy" | "dev0" | "faq" | "blog" | "tail";

export default function Test1Landing({
  signedIn = false,
  founder = false,
  posts = [],
  skills = [],
}: {
  signedIn?: boolean;
  founder?: boolean;
  /** live published blog/update posts for the rail after the questions. */
  posts?: CompanyPost[];
  /** the commons' skill cards, shown in the create act's third view. */
  skills?: CommunitySkill[];
}) {
  const [ioStage, setIoStage] = useState<Stage>("hero");
  const [seg, setSeg] = useState(-1); // -1 = outside the acts track
  const [booted, setBooted] = useState(false);
  // THE BIRTH — for the first beats netti is a small cell at the centre of the
  // solid ground; then one long glide grows it into the hero crown. `entering`
  // only ever flips false, so the entrance can never replay.
  const [entering, setEntering] = useState(true);
  // whose memory the you segment is showing — this index reaches all the way
  // into the look tween (the ground and the creature belong to the person)
  const [youP, setYouP] = useState(0);
  const youHeld = useRef(false);
  // the meet helps — scroll-stepped, one heading at a time
  const [helpIdx, setHelpIdx] = useState(0);

  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  // the branch map has a phone composition of its own (fewer, wider-spread
  // branches) — picked by width, not by squeezing the desktop one
  const [narrowFig, setNarrowFig] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 719px)");
    const on = () => setNarrowFig(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const rootRef = useRef<HTMLElement | null>(null);
  // the header's door comes in exactly as the hero's slides under the band
  useHeroDoor(rootRef, ".t1-hero__cta");
  const trackRef = useRef<HTMLElement | null>(null);
  const orgWrapRef = useRef<HTMLDivElement | null>(null);
  const veilRef = useRef<HTMLDivElement | null>(null);
  const ctaRef = useRef<HTMLElement | null>(null);
  const reveal = useReveal();

  // panes hold their last segment while the stage scrolls in/out — everything
  // was always there (law_always_there)
  const prevSegRef = useRef(0);
  if (seg >= 0) prevSegRef.current = seg;
  const paneSeg = prevSegRef.current;
  const act = ACT_OF_SEG(paneSeg);
  // the high-water mark: an act's name, once earned, stays offered
  const seenActRef = useRef(0);
  if (act > seenActRef.current) seenActRef.current = act;
  const seenAct = seenActRef.current;
  // the birth resolves into the hero crown after a beat — one glide, no cut.
  useEffect(() => {
    const t = window.setTimeout(() => setEntering(false), 620);
    return () => window.clearTimeout(t);
  }, []);

  /* the you segment cycles through its people on its own — paused under a
     resting cursor, steered by any click */
  useEffect(() => {
    if (seg !== 6 || reduced) return;
    const t = window.setInterval(() => {
      if (!youHeld.current) setYouP((i) => (i + 1) % YOU_PEOPLE.length);
    }, 7000);
    return () => window.clearInterval(t);
  }, [seg, reduced]);

  const lookKey =
    seg === 6 ? `acts6p${youP}`
    : seg >= 0 ? `acts${seg}`
    : entering && ioStage === "hero" ? "boot"
    : ioStage === "meet" ? `meet${helpIdx}`
    : ioStage;
  // THE BRIDGE — the last carry segment hands the pinned track to the
  // full-bleed creature. The navigator, the dek and the other chips go; the
  // netti chip holds alone for a beat, then it goes too.
  const bridging = seg === SEG_N - 1;

  /* `data-live` says JAVASCRIPT IS HERE. Every hidden-start reveal on this page
     is gated on it, so a build whose script never lands degrades to the whole
     page plainly visible instead of an invisible one under a stuck veil.
     It is written in a LAYOUT effect and set imperatively — not rendered — for
     two reasons: a layout effect lands before the first paint, so the reveals
     never flash visible and then hide; and staying out of the render output
     means no second pass and nothing for hydration to disagree about. */
  useLayoutEffect(() => {
    rootRef.current?.setAttribute("data-live", "1");
  }, []);

  /* THE BODY, once. The engine mounts asynchronously (it imports its shaders),
     so the patch is re-sent across the boot window — the same belt-and-braces
     the ink re-assert uses. After that nothing re-sends it: every section's
     look tints and ages this same body over the top. */
  useEffect(() => {
    const send = () => {
      window.dispatchEvent(new CustomEvent("innernet:organism-drive", { detail: { tuners: MEDIA_NETTI } }));
    };
    send();
    const ts = [400, 1200, 2600].map((ms) => window.setTimeout(send, ms));
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, []);

  /* boot: the veil lifts once the first look is applied. The veil also lifts on
     a CSS timer of its own (see .t1-boot) — this only lifts it sooner. */
  useEffect(() => {
    const t = window.setTimeout(() => setBooted(true), 380);
    return () => window.clearTimeout(t);
  }, []);

  /* which free section owns the screen — DETERMINISTIC from scroll position,
     the same discipline the acts track uses. An IntersectionObserver only
     tells you about ENTRIES into its band; a fast wheel, a jump, or a section
     taller than the band can skip an entry and leave the stage one section
     behind (the ground then lags: the questions read in dev green). Now the
     stage is simply "the last section whose top has crossed the 45% line",
     recomputed on every scroll — nothing to miss, nothing to re-fire. */
  const secTops = useRef<{ key: Stage; top: number }[]>([]);
  useEffect(() => {
    const secs = Array.from(document.querySelectorAll<HTMLElement>("[data-t1-sec]"));
    const measure = () => {
      secTops.current = secs
        .map((el) => ({ key: el.getAttribute("data-t1-sec") as Stage, top: el.getBoundingClientRect().top + window.scrollY }))
        .sort((a, b) => a.top - b.top);
    };
    let last: Stage | null = null;
    const pick = () => {
      const line = window.scrollY + window.innerHeight * 0.45;
      let cur: Stage = "hero";
      for (const s of secTops.current) if (s.top <= line) cur = s.key;
      if (cur !== last) { last = cur; setIoStage(cur); }
    };
    measure();
    pick();
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(() => { measure(); pick(); }).catch(() => { /* fine */ });
    }
    const t1 = window.setTimeout(() => { measure(); pick(); }, 1600);
    const t2 = window.setTimeout(() => { measure(); pick(); }, 4000);
    window.addEventListener("scroll", pick, { passive: true });
    window.addEventListener("resize", () => { measure(); pick(); });
    return () => {
      window.clearTimeout(t1); window.clearTimeout(t2);
      window.removeEventListener("scroll", pick);
    };
  }, []);

  /* the acts track: scroll position → SEGMENT (stepped; the one exception is
     the activity ride below, which is scroll-TIED on purpose) */
  const trackGeom = useRef({ top: 0, span: 1 });
  const helpsRef = useRef<HTMLDivElement | null>(null);
  const helpsGeom = useRef({ top: 0, span: 1 });
  const feedOuterRef = useRef<HTMLDivElement | null>(null);
  const feedInnerRef = useRef<HTMLDivElement | null>(null);
  const lastFeedAge = useRef<number | null>(null);
  // the skills grid rides the page exactly as the river does (see THE SKILLS
  // RIDE below). Its travel is measured, not read every scroll: the cards are
  // fixtures, so the only things that can change it are a resize or the fonts
  // landing — both of which already call measure().
  const skillsOuterRef = useRef<HTMLDivElement | null>(null);
  const skillsInnerRef = useRef<HTMLDivElement | null>(null);
  const skillsRange = useRef(0);
  // the developer act's pinned track — pin progress GROWS the branch map once
  // the creature has seated, and RETRACTS it before the stage unpins.
  const devTrackRef = useRef<HTMLDivElement | null>(null);
  const devFigRef = useRef<HTMLDivElement | null>(null);
  const devGeom = useRef({ top: 0, span: 1 });
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const r = track.getBoundingClientRect();
      trackGeom.current = { top: r.top + window.scrollY, span: Math.max(1, track.offsetHeight - window.innerHeight) };
      const ht = helpsRef.current;
      if (ht) {
        const hr = ht.getBoundingClientRect();
        helpsGeom.current = { top: hr.top + window.scrollY, span: Math.max(1, ht.offsetHeight - window.innerHeight) };
      }
      const dt = devTrackRef.current;
      if (dt) {
        const dr = dt.getBoundingClientRect();
        devGeom.current = { top: dr.top + window.scrollY, span: Math.max(1, dt.offsetHeight - window.innerHeight) };
      }
      // how far the skills grid has to travel to show its last card — 0 when
      // every card already fits the stage, which is what makes the ride
      // conditional rather than always-on. The pane is never display:none (it
      // hides with opacity), so this measures true whichever segment is lit.
      const so = skillsOuterRef.current;
      const si = skillsInnerRef.current;
      skillsRange.current = so && si ? Math.max(0, si.scrollHeight - so.clientHeight) : 0;
    };
    measure();
    // fonts and late images shift every track's top by a line or two — a
    // stale measure put a phone's segment math one seat off (a pane frozen
    // mid-blur). Re-measure once the faces land and again after the boot.
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(measure).catch(() => { /* the resize listener stands */ });
    }
    const t1 = window.setTimeout(measure, 1600);
    const t2 = window.setTimeout(measure, 4000);
    const onScroll = () => {
      const { top, span } = trackGeom.current;
      const p = (window.scrollY - top) / span;
      // ⚠️ THE CHANGE HAPPENS BEFORE THE LOCK. `p >= 0` is the moment the stage
      // PINS — adopting connect's look there meant the ground and the creature
      // were still turning while the section was already stuck to the screen.
      // A lead-in of most of a viewport (in track fractions) lets the whole
      // transition finish during the approach, so the pin lands on a section
      // that has already arrived.
      const lead = window.innerHeight * 0.8 / Math.max(1, span);
      const pl = p + lead;
      // THE HAND-OFF IS SHORT. Segments 0..8 share the track evenly; the last
      // "segment" (the wordless bloom into the meeting's seat) is a QUARTER
      // length — long enough for the glide, no dead scroll after it. So the
      // segment index is computed over 9.25 units, not 10.
      const UNITS = SEG_N - 1 + 0.25;
      const idx = Math.floor(Math.max(0, p) * UNITS);
      setSeg(pl < 0 || p > 1.02 ? -1 : Math.max(0, Math.min(SEG_N - 1, idx)));

      // THE DEV PIN — the branch map exists only in the middle of the pin:
      // it grows once netti has seated (the look glide has had its beat) and
      // retracts before the unpin, both as timed transitions the thresholds
      // TRIGGER — scroll never scrubs the drawing itself.
      const df = devFigRef.current;
      if (df) {
        const dg = devGeom.current;
        const dp2 = (window.scrollY - dg.top) / dg.span;
        const on = dp2 > 0.12 && dp2 < 0.8 ? "1" : null;
        if (df.getAttribute("data-in") !== on) {
          if (on) df.setAttribute("data-in", on);
          else df.removeAttribute("data-in");
        }
      }

      // THE ACTIVITY RIDE — there are no nested scroll regions on this page
      // (two of them used to trap the wheel and stall the whole journey). The
      // river reads with the PAGE: while its segment is active, page-scroll
      // progress inside the segment translates the list, and the same progress
      // de-ages netti — reading backwards through a memory is reading a
      // younger one. One transform write on one element; compositor-only.
      // the meet helps step with the page — one heading at a time, earned
      if (helpsRef.current) {
        const hg = helpsGeom.current;
        const hp = (window.scrollY - hg.top) / hg.span;
        if (hp > -0.1 && hp < 1.2) {
          setHelpIdx(Math.max(0, Math.min(MEET_BEATS - 1, Math.floor(hp * MEET_BEATS))));
        }
      }

      const outer = feedOuterRef.current;
      const inner = feedInnerRef.current;
      if (outer && inner) {
        // a REST before the river moves — the first ~28% of the segment holds
        // the list still so it can be read as it arrives, then the ride runs
        // through the remaining scroll
        const local = Math.min(1, Math.max(0, (p * (SEG_N - 1 + 0.25) - 7 - 0.28) / 0.72)); // 0..1 inside seg 7
        const range = Math.max(0, inner.scrollHeight - outer.clientHeight);
        inner.style.transform = `translateY(${(-local * range).toFixed(1)}px)`;
        const age = Math.round((0.92 - local * 0.5) * 50) / 50;
        if (lastFeedAge.current !== age && p * (SEG_N - 1 + 0.25) > 6.9 && p * (SEG_N - 1 + 0.25) < 8.1) {
          lastFeedAge.current = age;
          window.dispatchEvent(new CustomEvent("innernet:organism-age", { detail: { age } }));
        }
      }

      // THE SKILLS RIDE — the same law as the river above, for the same reason.
      // The commons grid used to be `overflow-y: auto`, which never engaged (the
      // box had no height, so it just grew past the pinned stage and the last
      // row was clipped away) — and the moment it HAD engaged it would have been
      // a nested scroll region: on a phone a finger inside it is eaten by the
      // grid and the page's whole scroll choreography stalls mid-act. So the
      // page carries it: one transform on one element, compositor-only, and no
      // gesture on this page ever belongs to anything but the page.
      //
      // It only moves when the cards genuinely outgrow the stage. At three
      // columns on a wide screen they usually all fit and `range` is 0 — then
      // nothing rides and the grid simply sits there, which is the right answer.
      const sInner = skillsInnerRef.current;
      if (sInner) {
        const range = skillsRange.current;
        // THE WINDOW: rest 0.4 → ride 0.5 → rest 0.1, and both rests are load-
        // bearing. The opening rest is 0.4 because that is exactly where a chip
        // seats you (segScroll lands at i + 0.4), so pressing "skills" always
        // arrives at the TOP of the list. The closing 0.1 is there because the
        // pane hands over to `carry` the instant this segment ends — a ride that
        // finished at 1.0 put the last card on screen and took it away in the
        // same frame, so nobody ever saw it. It lands, then it is read.
        const local = range > 0 ? Math.min(1, Math.max(0, (p * UNITS - 5 - 0.4) / 0.5)) : 0;
        sInner.style.transform = `translateY(${(-local * range).toFixed(1)}px)`;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const segScroll = useCallback((i: number) => {
    const { top, span } = trackGeom.current;
    window.scrollTo({ top: top + span * ((i + 0.4) / (SEG_N - 1 + 0.25)), behavior: "smooth" });
  }, []);

  /** jump to one of the meet beats.
   *
   *  ⚠️ It has to SCROLL, not just set the index. `helpIdx` is derived from the
   *  scroll position on every scroll event, so setting it directly would be
   *  overwritten by the very next frame of scrolling. Moving the page is what
   *  makes a click and the scroll agree about where you are. */
  const meetGo = useCallback((i: number) => {
    const { top, span } = helpsGeom.current;
    window.scrollTo({ top: top + span * ((i + 0.5) / MEET_BEATS), behavior: reduced ? "auto" : "smooth" });
  }, [reduced]);

  /* THE ACTS TICKER — the dashboard navigator's own behaviour (OsNavigator):
     the lit act glides to the FAR LEFT of the strip, acts already read slide
     off the left edge blurred and fading, and — once every earlier act has
     passed — wrapped copies trail in from the right so the row always reads
     forward. Offsets are measured off the DOM (the reveal animates a word's
     width over 0.9s, so a settle re-measure follows it). */
  const actsStripRef = useRef<HTMLDivElement | null>(null);
  const [actOffsets, setActOffsets] = useState<number[]>([]);
  useEffect(() => {
    const strip = actsStripRef.current;
    if (!strip) return;
    const measure = () => {
      const kids = Array.from(strip.children).slice(0, ACTS.length) as HTMLElement[];
      if (kids.length < ACTS.length) return;
      const next = kids.map((el) => el.offsetLeft);
      setActOffsets((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
    };
    measure();
    const t = window.setTimeout(measure, 980);
    const ro = new ResizeObserver(measure);
    ro.observe(strip);
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(measure).catch(() => { /* the RO stands */ });
    }
    return () => {
      window.clearTimeout(t);
      ro.disconnect();
    };
  }, [seenAct]);

  /* ── the stage tween: a BOUNDED transition on look change. ONE CONTINUOUS
        CREATURE — every seat change is a single glide of position, scale,
        colour and age. The old de-age/regrow cut (die down, be reborn) is
        gone: netti never shrinks away and comes back; it morphs and moves. ── */
  const lookRef = useRef<Look>(LOOKS.boot);
  const rafRef = useRef(0);
  const firstRef = useRef(true);
  const narrowRef = useRef(false);
  const inkRef = useRef<{ l: number | null; s: number | null; bg: string; img: string; age: number | null; dark: number | null }>({ l: null, s: null, bg: "", img: "", age: null, dark: null });

  const applyLook = useCallback((lk: Look, opts?: { paintGround?: boolean; paintHeavy?: boolean }) => {
    const body = document.body;
    const root = document.documentElement;
    const st = inkRef.current;
    // ⚠️ TWO CADENCES, NOT ONE. Everything about the ground used to skip frames
    // together at ~15fps, and the eye reads the BASE COLOUR — so a section
    // change stepped visibly, which is the stutter. A flat background-color is
    // a cheap paint; the multi-gradient wash and the veil's backdrop-filter
    // tint are the expensive ones. So the colour moves every frame and the
    // expensive layers keep their own slower cadence beneath it.
    const paintGround = opts?.paintGround !== false;
    const paintHeavy = opts?.paintHeavy !== false;
    const [r, g, b] = hslToRgb({ h: lk.g.h, s: lk.g.s, l: lk.g.l });
    const R = Math.round(r * 255), G = Math.round(g * 255), B = Math.round(b * 255);
    const bgCol = `rgb(${R}, ${G}, ${B})`;
    if (paintGround && bgCol !== st.bg) {
      body.style.backgroundColor = bgCol;
      // --bg follows the live ground so anything coloured "the ground" (the
      // endpoint bar's copy label, the menu veil) stays in contrast on every
      // segment. Guarded like the ink writes.
      document.documentElement.style.setProperty("--bg", bgCol);
      st.bg = bgCol;
    }
    const H = Math.round(lk.g.h);
    const S = Math.round(lk.g.s * 100);
    const L = Math.round(lk.g.l * 100);
    const lTop = Math.min(94, L + 15);
    const glow = `radial-gradient(150% 110% at 50% -12%, hsla(${H}, ${S}%, ${lTop}%, 0.4) 0%, transparent 58%)`;
    // THE HUE-SHIFTED UPPER HALF (the dashboard ground-dial feeling, in two
    // gradients): the top of every ground bends through NEIGHBOURING hues
    // rather than sitting as one flat colour, so the creature's own colours
    // find something to blend into. Left leans one way round the wheel, right
    // the other; both fade out by the middle of the screen, so the lower half
    // stays the honest ground the ink contrast was computed against.
    const wash = (at: string, dh: number, dl: number, a: number, size: string) =>
      `radial-gradient(${size} at ${at}, hsla(${(H + dh + 360) % 360}, ${Math.min(96, S + 6)}%, ${Math.min(92, L + dl)}%, ${a}) 0%, transparent 66%)`;
    const upper = `${wash("16% -6%", -24, 16, 0.4, "116% 74%")}, ${wash("86% -4%", 28, 11, 0.34, "108% 66%")}`;
    // a look with gradient poles also gets the full top→mid→bottom wash beneath
    // all of it (the hero's forest-to-teal ground)
    const hslStr = (c: HSL) => `hsl(${Math.round(c.h)}, ${Math.round(c.s * 100)}%, ${Math.round(c.l * 100)}%)`;
    const bgImg =
      lk.gT || lk.gB
        ? `${glow}, ${upper}, linear-gradient(180deg, ${hslStr(lk.gT ?? lk.g)} 0%, ${hslStr(lk.g)} 46%, ${hslStr(lk.gB ?? lk.g)} 100%)`
        : `${glow}, ${upper}`;
    if (paintHeavy && bgImg !== st.img) { body.style.backgroundImage = bgImg; st.img = bgImg; }
    // the ink flip — stepped to whole percents, written only on change (the
    // most-expensive-two-lines law from the live landing)
    const k = Math.min(1, Math.max(0, (luminance([r, g, b]) - 0.44) / 0.12));
    const kk = k * k * (3 - 2 * k);
    const inkL = Math.round(96 - kk * (96 - 12));
    const inkS = Math.round(16 + kk * (52 - 16));
    if (paintHeavy && inkL !== st.l) { root.style.setProperty("--ink-light", `${inkL}%`); st.l = inkL; }
    if (paintHeavy && inkS !== st.s) { root.style.setProperty("--ink-sat", `${inkS}%`); st.s = inkS; }

    const wrap = orgWrapRef.current;
    if (wrap) {
      const nx = narrowRef.current ? lk.xN ?? lk.x : lk.x;
      const ny = narrowRef.current ? lk.yN ?? lk.y : lk.y;
      // ⚠️ COMPOSITOR-ONLY SEATING. `left`/`top` are LAYOUT properties — writing
      // them 60×/s during a glide forced a layout pass on every frame of every
      // section change (the creature's box is huge, so that pass is not
      // cheap). The wrap now sits at 0/0 and moves by TRANSLATE alone, which the
      // compositor animates without ever waking layout. Scale rides the same
      // transform, so one write positions AND sizes the body.
      wrap.style.transform = `translate3d(${(nx * 100).toFixed(2)}vw, ${(ny * 100).toFixed(2)}vh, 0) scale(${(narrowRef.current ? lk.scN : lk.sc).toFixed(3)}) translate(-50%, -50%)`;
      wrap.style.opacity = lk.op.toFixed(3);
    }
    const veil = veilRef.current;
    if (veil) {
      veil.style.opacity = Math.max(0.01, lk.veil).toFixed(3);
      // the backdrop blur EXISTS only while the frost is visible (see .t1-veil
      // in test1.css) — toggled at 0.12 with a little hysteresis so a tween
      // hovering at the threshold never flickers the filter on and off.
      const frosted = veil.hasAttribute("data-frost");
      if (!frosted && lk.veil > 0.12) veil.setAttribute("data-frost", "");
      else if (frosted && lk.veil < 0.08) veil.removeAttribute("data-frost");
      // the frost's tint is a full-screen backdrop-filter surface — repainting
      // its background every frame is the single heaviest paint on the page. It
      // follows the ground at the GROUND's own cadence (~15fps), never faster.
      if (paintHeavy) veil.style.backgroundColor = `rgba(${R}, ${G}, ${B}, 0.42)`;
    }
    if (st.age == null || Math.abs(lk.age - st.age) > 0.004) {
      st.age = lk.age;
      window.dispatchEvent(new CustomEvent("innernet:organism-age", { detail: { age: lk.age } }));
    }
    // the veins channel is only carried while a look declares it; 0 maps back
    // onto the engine's own mint (venation 0.44) so leaving the developer act
    // can never leave the creature drier than it was born.
    const drive: Record<string, unknown> = { color: lk.org, bg: [r, g, b] };
    if (lk.veins !== undefined) drive.veins = 0.45 + lk.veins * 0.55;
    if (lk.glass !== undefined) drive.glass = 0.55 + lk.glass * 0.45;
    if (lk.rot !== undefined) drive.rotation = lk.rot;
    // the close's fuller body — measured up from the media config's own
    // abundance/motes, never from the engine's mint. AND: on a DARK ground the
    // interior cells surface (John, 2026-08-20 — "the cells inside become a lot
    // more visible" on dark). `kk` is the same smoothed ground-luminance the
    // ink flip runs on (1 = pale ground, 0 = deep), so darkness = 1 − kk, and
    // it lifts the cell channels — motes and nodes brighten, the inner light
    // rises a touch — while a pale ground leaves the body exactly as minted.
    // Guarded like the age dispatch so the tuners only cross the bus on change.
    const dark = 1 - kk;
    const cellLift = lk.cells ?? 0;
    if (cellLift > 0 || dark > 0.2 || (st.dark ?? 0) > 0.2) {
      if (st.dark == null || Math.abs(dark - st.dark) > 0.03 || cellLift > 0) {
        st.dark = dark;
        drive.tuners = {
          abundance: Math.min(1, MEDIA_NETTI.abundance + cellLift * 0.12 + dark * 0.05),
          motes: Math.min(1, MEDIA_NETTI.motes + cellLift * 0.3 + dark * 0.32),
          nodes: Math.min(1, MEDIA_NETTI.nodes + dark * 0.3),
          innerLight: Math.min(1, MEDIA_NETTI.innerLight + dark * 0.1),
        };
      }
    }
    window.dispatchEvent(new CustomEvent("innernet:organism-drive", { detail: drive }));
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    narrowRef.current = mq.matches;
    const onMq = () => {
      narrowRef.current = mq.matches;
      applyLook(lookRef.current);
    };
    mq.addEventListener("change", onMq);
    return () => mq.removeEventListener("change", onMq);
  }, [applyLook]);

  /* the theme boot writes its own ink onto :root shortly after hydration —
     after our first apply. Re-assert with cleared guards a few times across
     the boot window (the live landing holds its rAF open 2.3s for this). */
  useEffect(() => {
    const reassert = () => {
      inkRef.current = { ...inkRef.current, l: null, s: null, bg: "", img: "" };
      applyLook(lookRef.current);
    };
    const ts = [700, 1800, 3200].map((ms) => window.setTimeout(reassert, ms));
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [applyLook]);

  const prevKeyRef = useRef("boot");
  useEffect(() => {
    const target = lookFor(lookKey);
    const from: Look = { ...lookRef.current, g: { ...lookRef.current.g } };
    const wasBoot = prevKeyRef.current === "boot";
    prevKeyRef.current = lookKey;
    // one creature, one glide — a long seat change simply takes a little
    // longer; the birth (boot → hero) is the slowest, most deliberate one.
    // Section-to-section stays BRISK (John: the between-sections move read
    // too long), only the birth lingers.
    const far = Math.abs(target.y - from.y) > 0.45;
    const dur = firstRef.current || reduced ? 0 : wasBoot ? 2400 : far ? 1350 : 950;
    firstRef.current = false;
    cancelAnimationFrame(rafRef.current);
    // a look change always re-asserts, even if the theme wrote underneath us
    inkRef.current = { ...inkRef.current, l: null, s: null, bg: "", img: "" };

    // netti rests harder beneath a heavy frost — downsampled behind the veil
    setOrganismCalm("t1-veil", target.veil > 0.3 ? 0.6 : null);

    if (dur === 0) {
      lookRef.current = target;
      applyLook(target);
      return;
    }
    const t0 = performance.now();
    let lastGround = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / dur);
      const lk = lerpLook(from, target, easeInOut(t));
      lookRef.current = lk;
      // the BASE COLOUR every frame (that is the thing the eye follows), the
      // gradient wash + frost tint + ink on their own ~20fps cadence — and the
      // LAST frame always paints everything, so a tween can never end mid-colour
      const heavy = t >= 1 || now - lastGround > 50;
      if (heavy) lastGround = now;
      applyLook(lk, { paintHeavy: heavy });
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [lookKey, reduced, applyLook]);

  /* leave the page the way we found it */
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      document.body.style.backgroundColor = "";
      document.body.style.backgroundImage = "";
      document.documentElement.style.removeProperty("--ink-light");
      document.documentElement.style.removeProperty("--ink-sat");
      document.documentElement.style.removeProperty("--bg");
      setOrganismCalm("t1-veil", null);
      window.dispatchEvent(new CustomEvent("innernet:organism-age", { detail: { age: null } }));
    };
  }, []);

  // THE HOMEPAGE HAS NO SECTIONS IN THE MENU — deliberately, and this empty list
  // is the statement of it. Its "sections" are acts of one continuous scroll,
  // not places you can be, so offering them as a directory made the ≡ open into
  // a list of anchors (connect · create · carry · netti · …) instead of the
  // site. Every visitor should get the same thing here: the innernet root, with
  // the way in, company, docs, skills and the rest. Anything added to this array
  // reappears in the menu for whoever is standing on the page, so leave it empty.
  const menuSpace = useMemo(() => ({ id: "home", label: "homepage", items: [] }), []);

  // OURS IS PARKED (John, 2026-08-17): the shared space gets its own launch, so
  // nothing on a public page links to it — not even for a founder. The
  // extensions row still NAMES it as coming, with no door attached.
  const paneState = (i: number) => (i < paneSeg ? "before" : i === paneSeg ? "on" : "after");

  return (
    <main ref={rootRef} className="tl-root" data-landing="test1" data-stage={seg >= 0 ? "acts" : ioStage}>
      {/* the one living organism — every act reseats THIS body */}
      <div
        ref={orgWrapRef}
        className="tl-organism"
        aria-hidden
        style={{ left: 0, top: 0, transform: "translate3d(50vw, 6vh, 0) scale(1.1) translate(-50%, -50%)", zIndex: 1 }}
      >
        <MemoryOrganism
          driveBus
          ageBus
          grow
          warm
          veil
          veilTune={{ ...VEIL_DOTS, opacity: 0.8 }}
          live={false}
          publishHue
          /* the engine's own undersample is the ONE softness this page uses —
             no CSS blur anywhere over the creature (both filter passes that used
             to sit on it are gone; see .t1-veil and .tl-organism in test1.css).
             0.78 × the 0.9 DPR cap ≈ 0.7 of CSS pixels: on a 900px MacBook the
             1512px canvas is ~1.1M backing pixels instead of ~1.65M, and a
             soft bloom with no hard edges cannot tell. Below ~0.7 the petals'
             glass edges start to shimmer on a slow turn, so this is the floor. */
          renderScale={0.78}
          age={0.8}
          color={HERO_ORG}
          /* the chromatography dials only — SHAPE comes from MEDIA_NETTI */
          macros={{ bleed: 0.74, shimmer: 0.14, glow: 0, grain: 0, density: 0.88, form: 0.58 }}
        />
      </div>

      {/* the one frost — blur radius NEVER animates; opacity + tint only */}
      <div ref={veilRef} className="t1-veil" aria-hidden />

      {/* the site's own header — crumb, ≡ takeover, the door in */}
      {/* signed in, the top-right chip is a DOOR — pressing yourself opens your
          dashboard (the you-takeover stays for deeper pages like the docs). */}
      <SiteMenu trail={[]} founder={founder} signedIn={signedIn} hereKey="home" current={menuSpace} />

      {/* ── ACT 0 · HERO — the creature is born at the centre, grows into the
             crown, and everything it can hold ORBITS it — the dashboard
             orbit's own geometry, on a slow, endless revolve. The words hold
             the last third. ── */}
      <section className="t1-hero" data-t1-sec="hero" aria-label="a home for your memories">
        <div className="t1-hero__orbit">
          <LandingOrbit
            items={HERO_ORBIT}
            origin="top"
            /* the core sits a little INTO the box (not above it) so a phone's
               field fans from the heart of the bloom rather than its top edge */
            /* ⚠️ ON A PHONE THE CORE SITS ABOVE THE STAGE (a POSITIVE sink for
               a top origin). It used to sit 60px INSIDE it, which forced the
               innermost ring to a ~80px radius — a circle that small is a
               huddle at the crown, not an arc across the screen. Lifted out of
               the box, all three rings can be wide (≈145 · 246 · 347) and the
               eye reads three arcs instead of a knot. The exact lift is a
               trade: the higher the core, the flatter every arc reads — 0.12
               keeps a real bowl in the innermost circle while the crown still
               clears the headline. */
            sink={narrowFig ? 0.12 : 0.1}
            farPad={narrowFig ? 40 : 150}
            rate={0.05}
            /* ⚠️ ONE FIELD, TWO SECTIONS. The hero and the create act are the
               same concept — the only difference is the pool (the hero carries
               apps; create only files and folders) — so they share the same
               icon size (matchProjectsView, no scale) and, on a phone, the same
               spread geometry and the same unique-circle seating. Desktop keeps
               the curated 9·6 crown that was signed off (`ringCounts` wins over
               `fullCircle` on desktop inside LandingOrbit). */
            matchProjectsView
            spreadOnPhone
            /* the hero SHARES the page with its headline — but the crown is
               held off the words by the grid's own far bound (gridCap: the
               crest must clear the far-edge dissolve), not by starving the
               radius. The reach is left wide so WIDTH only binds on a phone
               tall enough that height no longer does. */
            spreadReach={1.05}
            /* the phone field is laid on the circular grid — even pitch, a
               collision-proof band, rings that turn at their own speeds. The
               grid sizes its own tile and icon, so `matchProjectsView` above
               only governs the desktop hero. */
            phoneGrid
            fullCircle
            rings={2}
            /* nine on the crown, six inside — a fuller field on a desktop */
            ringCounts={[9, 6]}
            active={ioStage === "hero" && seg < 0}
            /* the field arrives only AFTER the bloom — the birth glide runs
               ~3s (620ms boot + 2.4s grow), and the icons follow it */
            enterDelay={reduced ? 0 : 3100}
            // the dotted orbit lines the tiles ride, as on the evolved homepage
            threads
          />
        </div>
        <div className="t1-hero__copy">
          <h1 className="t1-hero__h t1-rise" style={{ "--d": 0 } as React.CSSProperties}>
            a home for your memories
          </h1>
          <p className="t1-hero__sub t1-rise" style={{ "--d": 140 } as React.CSSProperties}>
            innernet is an ai memory platform that, once connected, stays in the background
            and saves and carries your context no matter where you go or what app you use.
          </p>
          <div className="t1-hero__cta t1-rise" style={{ "--d": 280 } as React.CSSProperties}>
            {/* THE DOOR, as the evolved homepage stands it (/test/home): the
                solid door, the way to the developers once their page is open
                (a founder sees it already), and the one quiet line under it.
                The birth happens right here over the page; nothing navigates. */}
            <CtaStack
              signedIn={signedIn}
              onBegin={() => openCreateYours()}
              devHref={founder || gateOpen("developersPublic") ? "/developers" : ""}
            />
          </div>
        </div>
      </section>

      {/* ── THE ACTS — one pinned stage, seven segments, the navigator holds ── */}
      <section ref={trackRef} id="s-acts" className="t1-track" data-t1-sec="acts" aria-label="connect, create, carry">
        <div className="t1-stage" data-bridge={bridging || undefined}>
          <div className="t1-shell">
            <DirIconScaleStyle />
            {/* THE TITLES ARRIVE ONE AT A TIME, and the row is the dashboard
                navigator's ticker: only the act being worked on is offered
                (`create` appears the moment connect is done), the lit act
                slides to the FAR LEFT, and the acts already read exit left,
                blurred. Nothing reappears on the right — the narrative only
                ever moves forward. */}
            <nav className="t1-acts" aria-label="the three acts">
              <div
                ref={actsStripRef}
                className="t1-acts__strip"
                style={{ transform: `translateX(${-(actOffsets[act] ?? 0)}px)` }}
              >
                {ACTS.map((a, i) => (
                  <button
                    key={a}
                    type="button"
                    data-shown={i <= seenAct ? "1" : undefined}
                    data-past={i < act ? "1" : undefined}
                    aria-current={i === act ? "true" : undefined}
                    aria-hidden={i > seenAct || undefined}
                    tabIndex={i > seenAct ? -1 : undefined}
                    onClick={() => segScroll(i * 3)}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </nav>

            <div className="t1-deks">
              {DEKS.map((d, i) => (
                <p key={i} data-on={i === act}>{d}</p>
              ))}
            </div>

            <div className="t1-chiprows">
              <div className="t1-chiprow" data-on={act === 0} aria-hidden={act !== 0}>
                {(["mcp", "api", "extensions"] as const).map((label, i) => (
                  <button key={label} type="button" className="chip" data-on={paneSeg === i} onClick={() => segScroll(i)}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="t1-chiprow" data-on={act === 1} aria-hidden={act !== 1}>
                {(["projects", "tasks", "skills"] as const).map((label, i) => (
                  <button key={label} type="button" className="chip" data-on={paneSeg === 3 + i} onClick={() => segScroll(3 + i)}>
                    {label}
                  </button>
                ))}
              </div>
              {/* carry's views. The LAST chip is netti, and it is the bridge:
                  as segment 8 arrives every other chip fades and only this one
                  is left lit — then it goes too, and the creature has the
                  screen. `data-solo` drives that (see .t1-chiprow[data-solo]). */}
              {/* carry's views — you · activity. There is no netti chip: netti is
                  not a VIEW of your memory, it is the memory, and the section
                  that introduces it is the next one. */}
              <div className="t1-chiprow" data-on={act === 2} data-solo={bridging || undefined} aria-hidden={act !== 2}>
                {(["you", "activity", "story"] as const).map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    className="chip"
                    data-on={paneSeg === 6 + i}
                    onClick={() => segScroll(6 + i)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="t1-panes">
              {/* 0 · mcp — THE ENDPOINT IS THE CONNECTION, and handing it over is
                    the whole install story: there is nothing to download, and one
                    url reaches every tool that speaks mcp.
                    ⚠️ This box is the mcp link and only the mcp link. `npx
                    innernet` is a DIFFERENT wiring system — a cli that configures
                    the clients on one machine to point at this same endpoint — so
                    putting it here replaces the connection with a tool for
                    setting it up. It is offered in the api pane's fine print
                    instead, where someone wiring their own machine will meet it. */}
              <div className="t1-pane t1-pane--doc" data-state={paneState(0)} aria-hidden={paneSeg !== 0}>
                <div className="t1-row t1-stag" style={{ "--i": 0 } as React.CSSProperties} aria-label="surfaces that speak mcp">
                  {MCP_ROW.map((name) => (
                    <AppIcon key={name} name={name} size={46} />
                  ))}
                </div>
                <div className="t1-card t1-stag" style={{ "--i": 1 } as React.CSSProperties}>
                  <div className="t1-card__lead">
                    <OrganismSpinner size={15} tone="current" spin={false} />
                    <span>one url. every ai you already use reads and writes the same memory.</span>
                  </div>
                  <EndpointRow url="innernet.live/api/mcp" />
                  <p className="t1-card__foot">
                    visit <a href="/docs">/docs</a> for the per-client wiring
                  </p>
                </div>
              </div>

              {/* 1 · api — for the agents you build */}
              <div className="t1-pane t1-pane--doc" data-state={paneState(1)} aria-hidden={paneSeg !== 1}>
                <div className="t1-card t1-stag" style={{ "--i": 0 } as React.CSSProperties}>
                  <div className="t1-card__lead">
                    <OrganismSpinner size={15} tone="current" spin={false} />
                    <span>per-user memory for the agents you build.</span>
                  </div>
                  {/* the api said the way a developer would meet it: one call
                      that shows the shape (a bearer key, an intent, one memory
                      back), then the three verbs it has. The endpoint bar
                      that used to open this pane repeated the mcp pane.
                      ⚠️ `/context` is a GET — `-d` would make curl POST it — and
                      the server compares the scheme exactly (`Bearer `, capital
                      B, lib/api/auth.ts), so a copied lowercase `bearer` 401s.
                      This block is copy-pasteable; keep it runnable. */}
                  {/* This block is copy-pasteable, so it is checked against the
                      live api, not just read. Three things it has to get right:
                      ⚠️ `-G` — /context is a GET; the previous `-d intent=…`
                         version made curl POST and production answered 405.
                      ⚠️ `--data-urlencode` for the intent — plain `-d` does NOT
                         encode, and an intent with spaces makes a malformed
                         request line that never gets an http response at all.
                      ⚠️ `Bearer` with a capital B — lib/api/auth.ts compares the
                         scheme exactly, so a lowercase one 401s with a good key.
                      And the block does not wrap (.t1-req is a <pre>): a line
                      past ~48 characters is cut at the card edge. */}
                  <CopyBlock
                    code={`curl -G innernet.live/api/v1/context \\
  -H "Authorization: Bearer $INNERNET_KEY" \\
  -d slug=atlas \\
  --data-urlencode "intent=what did we decide"`}
                  />
                  <ul className="t1-api__verbs">
                    <li><code>context</code><span>ask the memory a question, get the slice that answers it</span></li>
                    <li><code>capture</code><span>hand it something worth keeping; it files and folds it</span></li>
                    <li><code>projects</code><span>the map, its dimensions and nodes, as plain json</span></li>
                  </ul>
                  {/* the fine print is where the cli belongs: someone reading the
                      api pane is wiring something themselves, and `npx innernet`
                      is the shortcut for wiring the clients on their own machine
                      to the endpoint above. It is not an alternative to the mcp
                      link — it is what points local tools at it. */}
                  <p className="t1-card__foot">
                    every one of the 43 tools your ai tools use, as plain rest. to wire the
                    tools on your own machine to the endpoint, run <code>npx innernet</code>.{" "}
                    <a href="/docs/api">the whole surface</a>
                  </p>
                </div>
              </div>

              {/* 2 · extensions — what's coming to ride along */}
              <div className="t1-pane t1-pane--doc" data-state={paneState(2)} aria-hidden={paneSeg !== 2}>
                <div className="t1-card t1-card--wide t1-stag" style={{ "--i": 0 } as React.CSSProperties}>
                  <div className="t1-card__lead">
                    <OrganismSpinner size={15} tone="current" spin={false} />
                    <span>wherever you already think, that is where it should be</span>
                  </div>
                  <div className="t1-soon">
                    {SURFACES.map((s) => {
                      // /ours is behind a closed release gate — the row links to
                      // the page only for someone who can already see it, and to
                      // the waitlist for everyone else (law: a gated route stays
                      // unlisted, and nothing on a public page discovers it)
                      const href = s.href;
                      const Row = href ? "a" : "div";
                      return (
                        <Row key={s.k} {...(href ? { href } : {})}>
                          <span className="t1-soon__ic">
                            {s.icon === "stack" ? (
                              /* ONE extension, two doors: chrome in front, safari
                                 stepping out behind it — both wearing the same
                                 tile so the row reads as one thing. */
                              <span className="t1-stack">
                                <AppIcon name="safari" size={26} className="t1-stack__b" />
                                <AppIcon name="chrome" size={30} className="t1-stack__f" />
                              </span>
                            ) : s.icon === "ours" ? (
                              <span className="t1-stack">
                                <AppIcon name="innernet" size={26} className="t1-stack__b" />
                                <AppIcon name="innernet" size={30} className="t1-stack__f" />
                              </span>
                            ) : s.icon === "macapp" ? (
                              /* the mac app's own icon — the real tile from
                                 apps/desktop, not a brand mark on a ground */
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src="/brand/mac-app-icon.png" alt="" width={30} height={30} className="t1-soon__mac" />
                            ) : (
                              <AppIcon name={s.icon} size={30} />
                            )}
                          </span>
                          <span className="t1-soon__body">
                            <span className="t1-soon__name">{s.name}</span>
                            <span className="t1-soon__desc">{s.d}</span>
                          </span>
                          <span className="t1-soon__tag" data-tag={s.tag}>{s.tag}</span>
                        </Row>
                      );
                    })}
                  </div>
                  <p className="t1-card__foot">
                    be first through each door. <a href="/signup">keep a seat</a>
                  </p>
                </div>
              </div>

              {/* 3 · create — the folder grid, the dashboard's own grammar */}
              <div className="t1-pane" data-state={paneState(3)} aria-hidden={paneSeg !== 3}>
                <div className="rg-grid" style={{ "--p": 1 } as React.CSSProperties}>
                  {GRID_ITEMS.map((it, i) => (
                    <div key={it.n} className="rg-tile t1-stag" style={{ "--i": i } as React.CSSProperties}>
                      <DirIcon icon={it.icon} symbol={it.symbol} size={74} />
                      <span className="rg-name" data-selected={it.sel || undefined}>{it.n}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 4 · tasks — organised under their projects */}
              <div className="t1-pane" data-state={paneState(4)} aria-hidden={paneSeg !== 4}>
                <div className="t1-taskgroups" style={{ "--p": 1 } as React.CSSProperties}>
                  {TASK_GROUPS.map((g, gi) => (
                    <div key={g.proj} className="t1-stag" style={{ "--i": gi } as React.CSSProperties}>
                      <div className="t1-taskgroup__name">{g.proj}</div>
                      <div className="rk-list">
                        {g.tasks.map((t) => (
                          <div key={t.t} className="rk-row" data-done={t.done || undefined} style={{ "--i": 0 } as React.CSSProperties}>
                            <span className="task-check" aria-hidden>
                              <svg viewBox="0 0 14 14" aria-hidden>
                                <path d="M3 7.4 6 10.1 11 4.2" />
                              </svg>
                            </span>
                            <span className="rk-body">
                              <span className="rk-title">{t.t}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5 · skills — the commons, in brief. (The orbit depiction that
                  used to sit here is parked, not deleted: LandingOrbit and
                  CREATE_ORBIT are untouched and the hero still runs the same
                  field, so bringing it back is re-mounting it.)
                  ⚠️ The cards do NOT link. The commons is not linked from
                  anywhere on this page yet — this view is a depiction of what
                  the skills you keep look like, the same way the projects and
                  tasks views depict a dashboard nobody can click into from
                  here. Give them an href the day /community is announced. */}
              <div className="t1-pane" data-state={paneState(5)} aria-hidden={paneSeg !== 5}>
                <div className="t1-skills t1-stag" style={{ "--i": 0 } as React.CSSProperties} ref={skillsOuterRef}>
                  <div className="cm-grid t1-skills__grid" ref={skillsInnerRef}>
                    {skills.map((sk, i) => (
                      <div key={sk.key} className="cm-card" style={{ "--i": Math.min(i, 9) } as React.CSSProperties}>
                        <span className="cm-card__top">
                          <span className="cm-card__cat">{sk.category}</span>
                          {sk.version && <span className="cm-card__ver">v{sk.version}</span>}
                        </span>
                        <span className="cm-card__t">{sk.title}</span>
                        <span className="cm-card__d">{sk.description}</span>
                        {sk.tags.length > 0 && (
                          <span className="cm-card__tags">
                            {sk.tags.slice(0, 3).map((t) => <span key={t} className="cm-tag">{t}</span>)}
                          </span>
                        )}
                        <span className="cm-card__by">
                          <BrandIcon name="innernet" size={18} />
                          <span className="cm-card__keeper">
                            kept by <b>{originName(sk.origin)}</b>
                          </span>
                          {sk.updated && (
                            <span className="cm-card__when"><span className="ink-dot" aria-hidden />{sk.updated}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 6 · carry · YOU — four imagined people on one stage. The
                  face advances, the strip picks, and the pick reaches the
                  page's look tween: ground, creature and card all turn to
                  whoever is on stage. */}
              <div
                className="t1-pane"
                data-state={paneState(6)}
                aria-hidden={paneSeg !== 6}
                onPointerEnter={() => { youHeld.current = true; }}
                onPointerLeave={() => { youHeld.current = false; }}
              >
                <YouPersona pi={youP} onPick={setYouP} />
              </div>

              {/* 7 · carry · ACTIVITY — the dashboard feed, verbatim components.
                  Scrolling this list is what DE-AGES netti: you are reading
                  backwards through the memory, so the creature grows younger as
                  you go. See onFeedScroll. */}
              <div className="t1-pane" data-state={paneState(7)} aria-hidden={paneSeg !== 7}>
                <div className="t1-act">
                  <p className="t1-kicker t1-stag" style={{ "--i": 0 } as React.CSSProperties}>
                    everything that ever landed — read it backwards
                  </p>
                  <div className="t1-act__view t1-stag" style={{ "--i": 1 } as React.CSSProperties} ref={feedOuterRef}>
                    <div className="t1-act__inner" ref={feedInnerRef}>
                    <div className="feed">
                      {FEED.map((f) => (
                        <div key={f.id} className="feed-item" style={{ "--cite": f.self ? "var(--cite-self)" : "var(--cite-project)" } as React.CSSProperties}>
                          <div className="feed-meta">
                            <BrandIcon name={f.source} size={13} className="feed-mark" />
                            <span className="feed-source">{f.source}</span>
                            <span className="feed-time">{f.time}</span>
                          </div>
                          <div className="feed-line">
                            <span className="feed-statement" data-static>
                              <span className="feed-where">{f.where}</span>
                              <span className="feed-verbkind" data-static>{` — ${f.verb} ${f.kind}`}</span>
                            </span>
                          </div>
                          <div className="feed-detail">
                            <span className="feed-rule" aria-hidden />
                            <p className="feed-context">
                              <span className="feed-verb">{f.verb}</span>{" "}
                              {f.before}
                              <span className="feed-cite feed-cite-flat"><span className="feed-cite-text">{f.cite}</span></span>
                              {f.after}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    </div>
                  </div>
                  <p className="t1-carry-note">
                    the same river your dashboard shows — every tool, every save, in one
                    place. keep scrolling and netti grows younger with you.
                  </p>
                </div>
              </div>

              {/* 8 · carry · STORY — the arc the beats add up to */}
              <div className="t1-pane" data-state={paneState(8)} aria-hidden={paneSeg !== 8}>
                <div className="t1-act">
                  <p className="t1-kicker t1-stag" style={{ "--i": 0 } as React.CSSProperties}>
                    the whole account, from the first note
                  </p>
                  <div className="t1-story t1-stag" style={{ "--i": 1 } as React.CSSProperties}>
                    {STORY.map((b, i) => (
                      <div key={b.t} className="t1-story__beat" style={{ "--i": i } as React.CSSProperties}>
                        <span className="t1-story__when">{b.when}</span>
                        <span className="t1-story__line" aria-hidden />
                        <div className="t1-story__body">
                          <span className="t1-story__t">{b.t}</span>
                          <p className="t1-story__d">{b.d}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="t1-carry-note">
                    activity is every beat. this is what they add up to — the answer to
                    &ldquo;catch me up&rdquo;, written from what actually happened.
                  </p>
                </div>
              </div>

              {/* 9 · the HAND-OFF. NOTHING here on purpose: the chrome is
                  fading, the creature alone blooms into the meeting's seat. */}
              <div className="t1-pane t1-pane--bridge" data-state={paneState(9)} aria-hidden={paneSeg !== 9} />
            </div>
          </div>
        </div>
      </section>

      {/* ── MEET NETTI — ONE pinned track, FOUR stepped panels. The bridge
             hands the creature to the CENTRE, wordless; "meet netti" pops
             beneath it. Then, at EQUAL scroll intervals, each next panel
             arrives from the right while the current one leaves left (the
             section-slide law) — the creature holding the top-centre from the
             second panel on, its colour and rotation changing with each. No
             scrub: a boundary TRIGGERS each move. ── */}
      <section id="s-meet" className="t1-meet" aria-label="meet netti">
        <div ref={helpsRef} className="t1-meet__track" data-t1-sec="meet">
          <div className="t1-meet__stage">
            {/* ⚠️ THE MEETING IS NOT ONE OF THE THREE. Panel 0 is the moment the
                creature is introduced — it has no sibling to page between, so it
                carries no marks and no click: you simply scroll on from it. From
                panel 1 the creature is locked to the top and the words below it
                become a set of three you can page through, so the marks appear
                and the whole text area becomes the control. */}
            <div
              className="t1-meet__panes"
              data-paged={helpIdx > 0 || undefined}
              {...(helpIdx > 0
                ? {
                    role: "button",
                    tabIndex: 0,
                    "aria-label": "next",
                    onClick: () => meetGo(helpIdx >= MEET_BEATS - 1 ? 1 : helpIdx + 1),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        meetGo(helpIdx >= MEET_BEATS - 1 ? 1 : helpIdx + 1);
                      }
                    },
                  }
                : {})}
            >
              {MEET_PANELS.map((m, i) => (
                <div
                  key={m.k}
                  className="t1-meet__pane"
                  data-state={i < helpIdx ? "before" : i === helpIdx ? "on" : "after"}
                  data-first={i === 0 || undefined}
                  aria-hidden={i !== helpIdx}
                >
                  <h2 className="t1-meet__h">{m.t}</h2>
                  <p className="t1-meet__sub">{m.d}</p>
                </div>
              ))}
            </div>
            {/* THREE marks — one per panel of the set, and each one a door to it.
                Absent during the meeting itself (there is nothing to page). */}
            {helpIdx > 0 && (
              <div className="t1-meet__marks">
                {MEET_PANELS.slice(1).map((m, i) => (
                  <button
                    key={m.k}
                    type="button"
                    aria-label={m.t}
                    aria-current={i + 1 === helpIdx ? "true" : undefined}
                    data-on={i + 1 === helpIdx || undefined}
                    onClick={(e) => { e.stopPropagation(); meetGo(i + 1); }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PRIVACY — the same six commitments the live site makes, said to the
             person deciding rather than to the engineer implementing. The
             mechanism stays as a quiet second line so the claim is still
             checkable; the closing admission is kept word for word. ── */}
      {/* ── PRIVACY — the evolved homepage's room, verbatim (RoomPrivacy): the
             commitments said to a person, on cards the vertical scroll walks
             sideways, the mechanism under each. `data-home` scopes its styles
             (home.css · launch.css) to this one room. ── */}
      <div id="s-privacy" className="t1-priv-room" data-t1-sec="privacy" data-home>
        <RoomPrivacy n="" />
      </div>

      {/* ── DEVELOP WITH INNERNET — ONE depiction, PINNED (the acts-track
             law): the creature settles at the bottom first, then the branch
             map grows out of it, holds while the tail of the track gives it
             time to be read — and RETRACTS before the stage unpins, so the
             lines never float loose behind the next section. The grow and the
             retract are both driven by pin progress (see the dev block in the
             scroll controller). ── */}
      <section id="s-dev" className="t1-dev" aria-label="develop with innernet">
        <div ref={devTrackRef} className="t1-dev__track" data-t1-sec="dev0">
          <div className="t1-dev__stage">
            <div className="t1-sec__in t1-rev" ref={reveal}>
              <p className="t1-dev__kick" style={{ "--i": 0 } as React.CSSProperties}>develop with innernet</p>
              <h2 className="t1-dev__h" style={{ "--i": 1 } as React.CSSProperties}>it&rsquo;s git for thinking.</h2>
              <p className="t1-dev__sub" style={{ "--i": 2 } as React.CSSProperties}>
                your context, versioned like code. branch it, roll it back, carry it into any tool.
                start at <a href="/docs">docs</a>
              </p>
            </div>
            <div ref={devFigRef} className="t1-dev__figrev">
              <ThreadFig
                className="t1-dev__fig t1-dev__fig--git"
                paths={narrowFig ? GIT_PATHS_N : GIT_PATHS}
                anchors={narrowFig ? GIT_ANCHORS_N : GIT_ANCHORS}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── QUESTIONS — the short answers, on a light sky ── */}
      <div data-t1-sec="faq">
        <FaqSection />
      </div>

      {/* ── WHAT WE'VE WRITTEN — the live blog, on the same light sky as the
             questions (one reading surface, two kinds of answer). The rail is
             the company page's own card in a row; it renders nothing at all
             when nothing is published. ── */}
      {posts.length > 0 && (
        <section className="t1-blog" data-t1-sec="blog" aria-label="from the blog">
          <div className="t1-sec__in t1-rev" ref={reveal}>
            <p className="t1-kicker" style={{ "--i": 0 } as React.CSSProperties}>from the blog</p>
            <h2 className="t1-blog__h" style={{ "--i": 1 } as React.CSSProperties}>
              what we&rsquo;re thinking about.
            </h2>
          </div>
          <div className="t1-blog__wrap t1-rev" ref={reveal}>
            <BlogRail posts={posts} />
          </div>
          <div className="t1-sec__in">
            <p className="t1-blog__all">
              <a href="/company">everything we&rsquo;ve published</a>
            </p>
          </div>
        </section>
      )}

      {/* the close — the site's one gateway */}
      <div data-t1-sec="tail">
        {/* ONE door at the close (John, 2026-10-22): the header's, top right,
            stays lit; the close carries the endpoint and its words only */}
        <FooterCta signedIn={signedIn} ctaRef={ctaRef} onBegin={openCreateYours} copy="explore" bar={false} door={false} />
        {/* the defined footer the evolved homepage carries: five columns of
            places, then the line, the badge and the socials */}
        <HomeFooter founder={founder} pricing={false} />
      </div>

      {/* first-paint veil — the hero ground until the controller takes over */}
      <div className="t1-boot" data-off={booted ? "1" : undefined} aria-hidden />
    </main>
  );
}
